<template>
  <div class="bsr-page py-3">
    <header class="bsr-topbar">
      <div class="bsr-topbar__brand">
        <span class="bsr-toolbar__accent" aria-hidden="true"></span>
        <h1>Analytics</h1>
        <button
          type="button"
          class="bsr-topbar__info"
          aria-label="About this report"
          :title="aboutText"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
      </div>
      <div class="page-tab-bar" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :class="['page-tab', { 'is-active': activeTab === tab.key }]"
          :aria-selected="activeTab === tab.key"
          @click="setTab(tab.key)"
        >
          <span class="page-tab__label">{{ tab.label }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="bsr-alert">{{ errorMessage }}</CAlert>

    <div class="bsr-filterbar">
      <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
      <div class="bsr-presets" role="group" aria-label="Quick date ranges">
        <button
          v-for="preset in presets"
          :key="preset.key"
          type="button"
          class="bsr-preset"
          :class="{ 'is-active': activePreset === preset.key }"
          @click="applyPreset(preset.key)"
        >
          {{ preset.label }}
        </button>
      </div>
      <span class="bsr-filterbar__spacer"></span>
      <span v-if="rangeLabel" class="bsr-filterbar__range">{{ rangeLabel }}</span>
      <button type="button" class="bsr-search-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadActiveTab">
        <svg v-if="!isLoading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span>{{ isLoading ? 'Loading…' : 'Search' }}</span>
      </button>
    </div>

    <!-- ============================================================ -->
    <!-- OVERVIEW TAB                                                  -->
    <!-- ============================================================ -->
    <template v-if="activeTab === 'overview'">
      <div v-if="isLoading && !overview" class="bsr-skeleton-grid">
        <div v-for="n in 6" :key="`sk-${n}`" class="bsr-skeleton-tile"></div>
      </div>

      <template v-else-if="overview">
        <div class="bsr-kpi-grid">
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Total Sales</span>
              <strong class="bsr-kpi__value">{{ formatCurrency(overview.totals.sales) }}</strong>
              <span class="bsr-kpi__sub">{{ overview.totals.orders }} orders</span>
            </div>
            <span class="bsr-kpi__icon" aria-hidden="true">
              <span class="bsr-kpi__glyph">₹</span>
            </span>
          </div>
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Avg Order Value</span>
              <strong class="bsr-kpi__value">{{ formatCurrency(overview.totals.avg_order_value) }}</strong>
              <span class="bsr-kpi__sub">across {{ overview.totals.orders }} orders</span>
            </div>
            <span class="bsr-kpi__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            </span>
          </div>
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Items Sold</span>
              <strong class="bsr-kpi__value">{{ formatNumber(overview.totals.items) }}</strong>
              <span class="bsr-kpi__sub">line-item quantity</span>
            </div>
            <span class="bsr-kpi__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </span>
          </div>
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Returns</span>
              <strong class="bsr-kpi__value bsr-kpi__value--warn">{{ formatNumber(overview.totals.returns) }}</strong>
              <span class="bsr-kpi__sub">{{ formatPercent(overview.totals.return_rate) }} return rate</span>
            </div>
            <span class="bsr-kpi__icon bsr-kpi__icon--warn" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            </span>
          </div>
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Return Value</span>
              <strong class="bsr-kpi__value bsr-kpi__value--warn">{{ formatCurrency(overview.totals.return_value) }}</strong>
              <span class="bsr-kpi__sub">grand-total of returned orders</span>
            </div>
            <span class="bsr-kpi__icon bsr-kpi__icon--warn" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </span>
          </div>
          <div class="bsr-kpi">
            <div class="bsr-kpi__text">
              <span class="bsr-kpi__label">Active Branches</span>
              <strong class="bsr-kpi__value">{{ overview.totals.branches_active }}<span class="bsr-kpi__denom">/{{ overview.totals.branches_total }}</span></strong>
              <span class="bsr-kpi__sub">branches with sales</span>
            </div>
            <span class="bsr-kpi__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </span>
          </div>
        </div>

        <div class="bsr-orgs-strip">
          <div
            v-for="org in displayOrganisations(overview.organisations)"
            :key="`org-strip-${org.id}`"
            class="bsr-org-tile"
          >
            <span class="bsr-org-tile__logo">
              <img
                v-if="orgLogo(org.id)"
                :src="orgLogo(org.id)"
                :alt="`${org.name} logo`"
                @error="onLogoError(org.id)"
              />
              <span v-else class="bsr-org-tile__initial" aria-hidden="true">{{ orgInitial(org.name) }}</span>
            </span>
            <div class="bsr-org-tile__body">
              <span class="bsr-org-tile__name">{{ org.name }}</span>
              <strong class="bsr-org-tile__sales">{{ formatCurrency(overviewByOrg(org.id).sales) }}</strong>
              <span class="bsr-org-tile__meta">
                {{ overviewByOrg(org.id).orders }} orders
                <span v-if="overviewByOrg(org.id).returns" class="bsr-org-tile__returns">· {{ overviewByOrg(org.id).returns }} returns</span>
              </span>
            </div>
          </div>
        </div>

        <section class="bsr-section bsr-section--chart">
          <header class="bsr-section__head">
            <h2>Daily Sales &amp; Orders</h2>
            <small class="bsr-section__hint">Revenue vs order volume · {{ overview.trend.length }} day{{ overview.trend.length === 1 ? '' : 's' }}</small>
          </header>
          <div class="bsr-section__body bsr-chart-body">
            <CChartBar
              v-if="overview.trend.length"
              :datasets="trendChartData.datasets"
              :labels="trendChartData.labels"
              :options="trendChartOptions"
              style="height: 280px;"
            />
            <p v-else class="bsr-empty">No sales in this range.</p>
          </div>
        </section>

        <div class="bsr-leaderboard-grid">
          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Branches</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">By sales</small>
                <button type="button" class="bsr-viewall" @click="openListPane('branches', 'Top Branches')">View all</button>
              </div>
            </header>
            <ol v-if="overview.top_branches.length" class="bsr-rank">
              <li
                v-for="(b, i) in overview.top_branches"
                :key="`tb-${b.branch_id}`"
                class="bsr-rank__item"
                @click="rowDetail('branches', b)"
              >
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ b.branch_name }}</span>
                  <span class="bsr-rank__sub">{{ b.state_name || '—' }} · {{ b.orders }} orders</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(b.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No branch activity.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Direct Store Sales</h2>
              <div class="bsr-head-actions">
                <button type="button" class="bsr-viewall" @click="openListPane('store_branches', 'Top Direct Store Sales')">View all</button>
              </div>
            </header>
            <ol v-if="(overview.top_store_branches || []).length" class="bsr-rank">
              <li
                v-for="(b, i) in overview.top_store_branches"
                :key="`tsb-${b.branch_id}`"
                class="bsr-rank__item"
                @click="rowDetail('store_branches', b)"
              >
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ b.branch_name }}</span>
                  <span class="bsr-rank__sub">{{ b.state_name || '—' }} · {{ b.orders }} store orders</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(b.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No store-counter sales.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Multi-Org Customers</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">Bought across MAP / MDI / MVP</small>
                <button type="button" class="bsr-viewall" @click="openListPane('multi_org_customers', 'Multi-Org Customers')">View all</button>
              </div>
            </header>
            <ol v-if="(overview.multi_org_customers || []).length" class="bsr-rank">
              <li v-for="(c, i) in overview.multi_org_customers" :key="`moc-${c.phone}-${i}`" class="bsr-rank__item" @click="rowDetail('multi_org_customers', c)">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ c.name || 'Unknown' }}</span>
                  <span class="bsr-rank__sub">
                    {{ c.phone }}
                    <span class="bsr-rank__orgs">
                      <span
                        v-for="oid in c.org_ids"
                        :key="`moc-org-${c.phone}-${oid}`"
                        class="bsr-org-badge"
                        :title="orgNameById(oid)"
                      >{{ orgShortById(oid) }}</span>
                    </span>
                    · {{ formatNumber(c.orders) }} orders
                  </span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(c.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No customers span multiple orgs.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Staff</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">By sales</small>
                <button type="button" class="bsr-viewall" @click="openListPane('staff', 'Top Staff')">View all</button>
              </div>
            </header>
            <ol v-if="overview.top_staff.length" class="bsr-rank">
              <li v-for="(s, i) in overview.top_staff" :key="`ts-${s.user_id}`" class="bsr-rank__item" @click="rowDetail('staff', s)">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ s.user_name }}</span>
                  <span class="bsr-rank__sub">{{ s.branch_name || 'No branch' }} · {{ s.orders }} orders</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(s.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No staff sales.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Products</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">By sales</small>
                <button type="button" class="bsr-viewall" @click="openListPane('products', 'Top Products')">View all</button>
              </div>
            </header>
            <ol v-if="overview.top_products.length" class="bsr-rank">
              <li v-for="(p, i) in overview.top_products" :key="`tp-${p.item_id || p.name}-${i}`" class="bsr-rank__item" @click="rowDetail('products', p)">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ p.name }}</span>
                  <span class="bsr-rank__sub">{{ formatNumber(p.quantity) }} units sold</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(p.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No product activity.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Districts</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">By sales</small>
                <button type="button" class="bsr-viewall" @click="openListPane('districts', 'Top Districts')">View all</button>
              </div>
            </header>
            <ol v-if="(overview.top_districts || []).length" class="bsr-rank">
              <li v-for="(d, i) in overview.top_districts" :key="`tdist-${d.district}-${i}`" class="bsr-rank__item" @click="rowDetail('districts', d)">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ d.district }}</span>
                  <span class="bsr-rank__sub">{{ d.state_name || '—' }} · {{ formatNumber(d.orders) }} orders</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(d.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No district activity.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Frequent Customers</h2>
              <div class="bsr-head-actions">
                <small class="bsr-section__hint">By order count</small>
                <button type="button" class="bsr-viewall" @click="openListPane('customers', 'Frequent Customers')">View all</button>
              </div>
            </header>
            <ol v-if="(overview.top_customers || []).length" class="bsr-rank">
              <li v-for="(c, i) in overview.top_customers" :key="`tcust-${c.phone}-${i}`" class="bsr-rank__item" @click="rowDetail('customers', c)">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ c.name || 'Unknown' }}</span>
                  <span class="bsr-rank__sub">{{ c.phone }}<template v-if="c.district"> · {{ c.district }}</template> · {{ formatCurrency(c.sales) }}</span>
                </div>
                <strong class="bsr-rank__value bsr-rank__value--count">
                  {{ formatNumber(c.orders) }}<span class="bsr-rank__unit">orders</span>
                </strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No repeat customers.</p>
          </section>

          <section class="bsr-section bsr-leaderboard">
            <header class="bsr-section__head">
              <h2>Top Sales by Mode</h2>
              <small class="bsr-section__hint">Which channel sold most</small>
            </header>
            <ol v-if="topSalesByMode.length" class="bsr-rank">
              <li v-for="(m, i) in topSalesByMode" :key="`tmode-${m.key}-${i}`" class="bsr-rank__item bsr-rank__item--static">
                <span class="bsr-rank__pos">{{ i + 1 }}</span>
                <div class="bsr-rank__body">
                  <span class="bsr-rank__name">{{ m.label }}</span>
                  <span class="bsr-rank__sub">{{ formatNumber(m.orders) }} orders</span>
                </div>
                <strong class="bsr-rank__value">{{ formatCurrency(m.sales) }}</strong>
              </li>
            </ol>
            <p v-else class="bsr-empty bsr-empty--inline">No sales-mode data in this range.</p>
          </section>
        </div>

        <div class="bsr-mix-grid">
          <section class="bsr-section bsr-section--chart">
            <header class="bsr-section__head">
              <h2>Payment Mix</h2>
              <small class="bsr-section__hint">Sales share by method</small>
            </header>
            <div class="bsr-section__body bsr-mix-body">
              <CChartDoughnut
                v-if="paymentMixChart.labels.length"
                :datasets="paymentMixChart.datasets"
                :labels="paymentMixChart.labels"
                :options="doughnutChartOptions"
                style="height: 260px;"
              />
              <p v-else class="bsr-empty">No payment data in this range.</p>
            </div>
          </section>

          <section class="bsr-section bsr-section--chart">
            <header class="bsr-section__head">
              <h2>Order Type Mix</h2>
              <small class="bsr-section__hint">Sales by order type</small>
            </header>
            <div class="bsr-section__body bsr-mix-body">
              <CChartBar
                v-if="orderTypeChart.labels.length"
                :datasets="orderTypeChart.datasets"
                :labels="orderTypeChart.labels"
                :options="barChartOptions"
                style="height: 260px;"
              />
              <p v-else class="bsr-empty">No order-type data in this range.</p>
            </div>
          </section>

          <section class="bsr-section bsr-section--chart">
            <header class="bsr-section__head">
              <h2>Calls by Type</h2>
              <small class="bsr-section__hint">{{ formatNumber(overview.calls_total || 0) }} calls placed</small>
            </header>
            <div class="bsr-section__body bsr-mix-body">
              <CChartBar
                v-if="callTypeChart.labels.length"
                :datasets="callTypeChart.datasets"
                :labels="callTypeChart.labels"
                :options="callBarChartOptions"
                style="height: 260px;"
              />
              <p v-else class="bsr-empty">No calls in this range.</p>
            </div>
          </section>

          <section class="bsr-section bsr-section--chart">
            <header class="bsr-section__head">
              <h2>Fulfillment Status</h2>
              <small class="bsr-section__hint">Orders by pipeline stage</small>
            </header>
            <div class="bsr-section__body bsr-mix-body">
              <CChartDoughnut
                v-if="fulfillmentChart.labels.length"
                :datasets="fulfillmentChart.datasets"
                :labels="fulfillmentChart.labels"
                :options="fulfillmentChartOptions"
                style="height: 260px;"
              />
              <p v-else class="bsr-empty">No orders in this range.</p>
            </div>
          </section>

          <section class="bsr-section bsr-section--chart">
            <header class="bsr-section__head">
              <h2>New vs Repeat Customers</h2>
              <small class="bsr-section__hint">{{ formatNumber(retentionTotals.customers) }} customers · {{ formatPercent(retentionTotals.repeatRate) }} repeat</small>
            </header>
            <div class="bsr-section__body bsr-mix-body">
              <CChartDoughnut
                v-if="retentionChart.labels.length"
                :datasets="retentionChart.datasets"
                :labels="retentionChart.labels"
                :options="retentionChartOptions"
                style="height: 260px;"
              />
              <p v-else class="bsr-empty">No customer orders in this range.</p>
            </div>
          </section>
        </div>
      </template>
    </template>

    <!-- ============================================================ -->
    <!-- BY BRANCH / BY STATE TABS                                     -->
    <!-- ============================================================ -->
    <template v-if="activeTab === 'branch' || activeTab === 'state'">
      <div class="bsr-view-bar">
        <div class="bsr-view-bar__title">
          <h2>{{ activeTab === 'branch' ? 'Sales by Branch' : 'Sales by State' }}</h2>
          <span v-if="tableRows.length" class="bsr-totals">
            <span class="bsr-totals__chip">
              {{ activeTab === 'branch' ? 'Branches' : 'States' }}
              <strong>{{ tableRows.length }}</strong>
            </span>
            <span class="bsr-totals__chip">Orders <strong>{{ formatNumber(grandTotalOrders) }}</strong></span>
            <span class="bsr-totals__chip">Sales <strong>{{ formatCurrency(grandTotalSales) }}</strong></span>
          </span>
        </div>
        <div class="bsr-view-toggle" role="group" aria-label="View mode">
          <button
            type="button"
            class="bsr-view-toggle__btn"
            :class="{ 'is-active': viewMode === 'table' }"
            :aria-pressed="viewMode === 'table'"
            title="Table view"
            @click="setViewMode('table')"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
            <span>Table</span>
          </button>
          <button
            type="button"
            class="bsr-view-toggle__btn"
            :class="{ 'is-active': viewMode === 'grid' }"
            :aria-pressed="viewMode === 'grid'"
            title="Grid view"
            @click="setViewMode('grid')"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            <span>Grid</span>
          </button>
        </div>
      </div>

      <!-- TABLE VIEW -->
      <BaseTable
        v-if="viewMode === 'table'"
        :title="null"
        :items="tableRows"
        :columns="activeColumns"
        :loading="isLoading"
        :default-sort="{ column: 'total_sales', direction: 'desc' }"
        :empty-text="emptyText"
        :row-class-fn="activeTab === 'branch' ? () => 'bsr-row-clickable' : null"
        @row-click="onRowClick"
      >
        <template #cell-branch_name="{ row }">
          <span class="bsr-branch-cell">
            <span>{{ row.branch_name }}</span>
            <svg class="bsr-branch-cell__chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
        </template>

        <template #cell-state_name="{ row }">
          <span class="bsr-state-pill">{{ row.state_name || '—' }}</span>
        </template>

        <template
          v-for="org in organisations"
          :slot="`cell-org_${org.id}`"
          slot-scope="{ row }"
        >
          <span :key="`org-${org.id}-${row[orgRowKey]}`" :class="{ 'bsr-zero': !row[`org_${org.id}_sales`] }">
            {{ formatCurrency(row[`org_${org.id}_sales`] || 0) }}
          </span>
        </template>

        <template #cell-total_sales="{ row }">
          <strong>{{ formatCurrency(row.total_sales) }}</strong>
        </template>
      </BaseTable>

      <!-- GRID VIEW -->
      <template v-else>
        <div v-if="isLoading && !tableRows.length" class="bsr-card-grid">
          <div v-for="n in 6" :key="`csk-${n}`" class="bsr-card bsr-card--skeleton" aria-hidden="true"></div>
        </div>

        <p v-else-if="!tableRows.length" class="bsr-empty bsr-card-empty">{{ emptyText }}</p>

        <div v-else class="bsr-card-grid">
          <component
            v-for="row in tableRows"
            :key="`card-${activeTab}-${row[orgRowKey]}`"
            :is="activeTab === 'branch' ? 'button' : 'div'"
            :type="activeTab === 'branch' ? 'button' : null"
            :class="['bsr-card', { 'bsr-card--clickable': activeTab === 'branch' }]"
            @click="activeTab === 'branch' ? openBranchDrillDown(row) : null"
          >
            <div
              :class="[
                'bsr-card__hero',
                `bsr-card__hero--${branchAvatarTone(activeTab === 'branch' ? row.branch_name : row.state_name)}`,
                { 'bsr-card__hero--photo': activeTab === 'branch' && row.image_url }
              ]"
            >
              <img
                v-if="activeTab === 'branch' && row.image_url"
                :src="row.image_url"
                :alt="`${row.branch_name} image`"
                class="bsr-card__hero-image"
                @error="row.image_url = null"
              />
              <span v-else class="bsr-card__hero-letter" aria-hidden="true">{{ branchInitial(activeTab === 'branch' ? row.branch_name : row.state_name) }}</span>
              <span class="bsr-card__hero-pattern" aria-hidden="true"></span>
              <span class="bsr-card__hero-chip" :title="formatCurrency(row.total_sales)">
                <span class="bsr-card__hero-chip-label">Total Sales</span>
                <strong class="bsr-card__hero-chip-value">{{ formatCurrencyCompact(row.total_sales) }}</strong>
              </span>
              <span v-if="activeTab === 'branch'" class="bsr-card__hero-tag">Branch</span>
              <span v-else class="bsr-card__hero-tag">State</span>
            </div>

            <div class="bsr-card__body">
              <div class="bsr-card__name-row">
                <h3 class="bsr-card__name">{{ activeTab === 'branch' ? row.branch_name : row.state_name }}</h3>
                <span class="bsr-card__verify" aria-hidden="true" :title="activeTab === 'branch' ? 'Branch active' : 'State active'">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 1l2.39 4.84L20 7.07l-4 3.83.94 5.5L12 13.77l-4.94 2.63L8 10.9 4 7.07l5.61-1.23z" opacity="0.0"/><path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
              </div>
              <p class="bsr-card__tagline">
                <template v-if="activeTab === 'branch'">
                  <svg class="bsr-card__tagline-icon" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ row.state_name || '—' }}
                  <span class="bsr-card__dot">·</span>
                  {{ formatNumber(row.total_orders) }} order{{ row.total_orders === 1 ? '' : 's' }}
                </template>
                <template v-else>
                  <svg class="bsr-card__tagline-icon" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/></svg>
                  {{ row.branch_count }} {{ row.branch_count === 1 ? 'branch' : 'branches' }}
                  <span class="bsr-card__dot">·</span>
                  {{ formatNumber(row.total_orders) }} order{{ row.total_orders === 1 ? '' : 's' }}
                </template>
              </p>

              <div v-if="organisations.length" class="bsr-card__stats">
                <div
                  v-for="org in organisations"
                  :key="`cardstat-${row[orgRowKey]}-${org.id}`"
                  class="bsr-card__stat"
                  :class="{ 'is-zero': !row[`org_${org.id}_sales`] }"
                  :title="`${org.name}: ${formatCurrency(row[`org_${org.id}_sales`] || 0)}`"
                >
                  <strong>{{ formatCurrencyCompact(row[`org_${org.id}_sales`] || 0) }}</strong>
                  <span>{{ shortOrgName(org.name) }}</span>
                </div>
              </div>

              <span v-if="activeTab === 'branch'" class="bsr-card__cta">
                <span>View Details</span>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </span>
            </div>
          </component>
        </div>
      </template>
    </template>

    <!-- ============================================================ -->
    <!-- BRANCH DRILL-DOWN SLIDE-OVER                                  -->
    <!-- ============================================================ -->
    <transition name="bsr-slide">
      <div v-if="drillBranch" class="bsr-drill" role="dialog" :aria-label="`Branch details: ${drillBranch.branch_name}`">
        <div class="bsr-drill__backdrop" @click="closeBranchDrillDown"></div>
        <aside class="bsr-drill__panel">
          <header class="bsr-drill__head">
            <span class="bsr-drill__avatar" aria-hidden="true">
              <img
                v-if="(insights && insights.branch && insights.branch.image_url) || drillBranch.image_url"
                :src="(insights && insights.branch && insights.branch.image_url) || drillBranch.image_url"
                :alt="`${drillBranch.branch_name} image`"
                @error="drillBranch.image_url = null"
              />
              <span v-else class="bsr-drill__avatar-initial">{{ branchInitial(drillBranch.branch_name) }}</span>
            </span>
            <div class="bsr-drill__head-text">
              <span class="bsr-drill__eyebrow">Branch</span>
              <h2>{{ drillBranch.branch_name }}</h2>
              <span class="bsr-drill__sub">
                {{ drillBranch.state_name || '—' }}
                <span v-if="rangeLabel"> · {{ rangeLabel }}</span>
              </span>
            </div>
            <button type="button" class="bsr-drill__close" aria-label="Close" @click="closeBranchDrillDown">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>

          <div class="bsr-drill__body">
            <div v-if="drillLoading" class="bsr-drill__loading">
              <div v-for="n in 4" :key="`dsk-${n}`" class="bsr-skeleton-tile"></div>
            </div>

            <template v-else-if="insights">
              <div class="bsr-drill__kpis">
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Sales</span>
                    <strong class="bsr-kpi__value">{{ formatCurrency(insights.totals.sales) }}</strong>
                    <span class="bsr-kpi__sub">{{ insights.totals.orders }} orders</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <span class="bsr-kpi__glyph">₹</span>
                  </span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Avg Order</span>
                    <strong class="bsr-kpi__value">{{ formatCurrency(insights.totals.avg_order_value) }}</strong>
                    <span class="bsr-kpi__sub">value per order</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                  </span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Items</span>
                    <strong class="bsr-kpi__value">{{ formatNumber(insights.totals.items) }}</strong>
                    <span class="bsr-kpi__sub">units sold</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                  </span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Returns</span>
                    <strong class="bsr-kpi__value bsr-kpi__value--warn">{{ formatNumber(insights.totals.returns) }}</strong>
                    <span class="bsr-kpi__sub">{{ formatPercent(insights.totals.return_rate) }} return rate</span>
                  </div>
                  <span class="bsr-kpi__icon bsr-kpi__icon--warn" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                  </span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Staff Active</span>
                    <strong class="bsr-kpi__value">{{ insights.totals.staff_count }}</strong>
                    <span class="bsr-kpi__sub">with sales</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </span>
                </div>
              </div>

              <div v-if="insights.org_sales && insights.org_sales.length" class="bsr-drill__orgs">
                <div
                  v-for="org in insights.org_sales"
                  :key="`drillorg-${org.id}`"
                  class="bsr-orgstat"
                  :class="{ 'is-zero': !org.sales }"
                  :title="`${org.name}: ${formatCurrency(org.sales)} · ${formatNumber(org.orders)} orders`"
                >
                  <span class="bsr-orgstat__code">{{ shortOrgName(org.name) }}</span>
                  <strong class="bsr-orgstat__value">{{ formatCurrency(org.sales) }}</strong>
                  <span class="bsr-orgstat__sub">{{ formatNumber(org.orders) }} order{{ org.orders === 1 ? '' : 's' }}</span>
                </div>
              </div>

              <div class="bsr-drill__tabs" role="tablist">
                <button
                  v-for="tab in drillTabs"
                  :key="tab.key"
                  type="button"
                  role="tab"
                  :class="['bsr-drill__tab', { 'is-active': drillTab === tab.key }]"
                  :aria-selected="drillTab === tab.key"
                  @click="drillTab = tab.key"
                >
                  {{ tab.label }}
                  <span v-if="tab.count != null" class="bsr-drill__tab-count">{{ tab.count }}</span>
                </button>
              </div>

              <div class="bsr-drill__toolbar">
                <button
                  type="button"
                  class="bsr-export-btn bsr-export-btn--sm"
                  :disabled="drillExporting || !activeDrillTable || !activeDrillTable.rows.length"
                  title="Export this table to Excel"
                  @click="exportDrillTable"
                >
                  <svg v-if="!drillExporting" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  <span>{{ drillExporting ? 'Exporting…' : 'Export to Excel' }}</span>
                </button>
              </div>

              <div class="bsr-drill__pane">
                <!-- ITEMWISE -->
                <BaseTable
                  v-if="drillTab === 'itemwise'"
                  title="Itemwise Report"
                  :items="insights.itemwise_report || []"
                  :columns="itemwiseColumns"
                  :loading="false"
                  :default-sort="{ column: 'quantity', direction: 'desc' }"
                  empty-text="No itemwise records in this period."
                />

                <!-- SALES BY PERSON -->
                <BaseTable
                  v-else-if="drillTab === 'sales'"
                  title="Sales By Person"
                  :items="filteredSalesByPerson"
                  :columns="salesByPersonColumns"
                  :loading="false"
                  :sticky-first-column="true"
                  :default-sort="{ column: 'total_orders', direction: 'desc' }"
                  :empty-text="salesByPersonSearch ? `No people match “${salesByPersonSearch}”.` : 'No sales-by-person records in this period.'"
                >
                  <template #toolbar>
                    <div class="sbp-search">
                      <svg class="sbp-search__icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                        <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M10 4a6 6 0 1 0 3.9 10.6l4.2 4.2 1.4-1.4-4.2-4.2A6 6 0 0 0 10 4Z"/>
                      </svg>
                      <input
                        v-model="salesByPersonSearch"
                        type="search"
                        class="sbp-search__input"
                        placeholder="Search person…"
                        aria-label="Search sales by person"
                      />
                      <button
                        v-if="salesByPersonSearch"
                        type="button"
                        class="sbp-search__clear"
                        aria-label="Clear search"
                        @click="salesByPersonSearch = ''"
                      >×</button>
                    </div>
                  </template>
                  <template #cell-co_order="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_bottles="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_kgs_sold="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_farm_bottles="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <!-- Same net (received − handed off) pattern as the c/o
                       count columns above, just in currency. -->
                  <template #cell-co_shipping_amount="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatCurrency(value) : '—' }}</span>
                  </template>
                  <template #cell-co_sales_ex_shipping="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatCurrency(value) : '—' }}</span>
                  </template>
                </BaseTable>

                <!-- STAFF PRODUCT SALES -->
                <BaseTable
                  v-else-if="drillTab === 'staff_products'"
                  title="Staff Product Sales"
                  :items="staffProductRows"
                  :columns="staffProductColumns"
                  :loading="false"
                  :sticky-first-column="true"
                  :default-sort="{ column: 'total', direction: 'desc' }"
                  empty-text="No staff product sales in this period."
                />

                <!-- ITEM RETURN REPORT -->
                <BaseTable
                  v-else-if="drillTab === 'item_returns'"
                  title="Item Return Report"
                  :items="insights.item_return_report || []"
                  :columns="itemReturnReportColumns"
                  :loading="false"
                  :default-sort="{ column: 'returned_quantity', direction: 'desc' }"
                  empty-text="No item returns in this period."
                />

                <!-- RETURNS (processed) -->
                <BaseTable
                  v-else-if="drillTab === 'returns'"
                  title="Returns"
                  :items="insights.processed_returns_report || []"
                  :columns="processedReturnsColumns"
                  :loading="false"
                  :default-sort="{ column: 'return_date', direction: 'desc' }"
                  empty-text="No returns processed in this period."
                >
                  <template #cell-sl_no="{ index }">{{ index + 1 }}</template>
                  <template #cell-order_number="{ value }">{{ value || '—' }}</template>
                  <template #cell-customer="{ row, value }">
                    <div class="bsr-cust-cell">
                      <div class="bsr-cust-cell__name">{{ value }}</div>
                      <div v-if="row.customer_phone" class="bsr-cust-cell__phone">{{ row.customer_phone }}</div>
                    </div>
                  </template>
                  <template #cell-products="{ value }">
                    <ul v-if="Array.isArray(value) && value.length" class="bsr-ret-products">
                      <li v-for="(prod, i) in value" :key="i" class="bsr-ret-products__item">
                        <span class="bsr-ret-products__name">{{ prod.name }}</span>
                        <span class="bsr-ret-products__qty">×{{ formatNumber(prod.quantity) }}</span>
                      </li>
                    </ul>
                    <span v-else>—</span>
                  </template>
                </BaseTable>

                <!-- STAFF RETURNS -->
                <BaseTable
                  v-else-if="drillTab === 'staff_returns'"
                  title="Staff Returns"
                  :items="insights.staff_return_report || []"
                  :columns="staffReturnReportColumns"
                  :loading="false"
                  :sticky-first-column="true"
                  :default-sort="{ column: 'returns', direction: 'desc' }"
                  empty-text="No staff returns in this period."
                >
                  <template #cell-co_orders_returned="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_bottles_returned="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_kgs_returned="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-co_farm_bottles_returned="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value) }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                  <template #cell-products="{ value }">
                    <ul v-if="Array.isArray(value) && value.length" class="bsr-ret-products">
                      <li v-for="(prod, i) in value" :key="i" class="bsr-ret-products__item">
                        <span class="bsr-ret-products__name">{{ prod.name }}</span>
                        <span class="bsr-ret-products__qty">×{{ formatNumber(prod.quantity) }}</span>
                      </li>
                    </ul>
                    <span v-else>—</span>
                  </template>
                </BaseTable>

                <!-- ROLE-WISE -->
                <BaseTable
                  v-else-if="drillTab === 'roles'"
                  title="Role-Wise Report"
                  :items="insights.role_wise_report || []"
                  :columns="roleWiseColumns"
                  :loading="false"
                  :default-sort="{ column: 'total_orders', direction: 'desc' }"
                  empty-text="No role activity in this period."
                >
                  <template #cell-role="{ value }">
                    <span class="bsr-state-pill">{{ value }}</span>
                  </template>
                  <template #cell-co_orders="{ value }">
                    <span :class="{ 'bsr-co-muted': !Number(value), 'bsr-co-neg': Number(value) < 0 }">{{ Number(value) ? formatNumber(value) : '—' }}</span>
                  </template>
                </BaseTable>

                <!-- SHIPPING -->
                <BaseTable
                  v-else-if="drillTab === 'shipping'"
                  title="Shipping Report"
                  :items="insights.shipping_report || []"
                  :columns="shippingReportColumns"
                  :loading="false"
                  :default-sort="{ column: 'pending', direction: 'desc' }"
                  empty-text="No shipping activity in this period."
                >
                  <template #cell-courier="{ value }">
                    <span class="bsr-state-pill">{{ value }}</span>
                  </template>
                </BaseTable>

                <!-- SALES BY DISTRICT -->
                <BaseTable
                  v-else-if="drillTab === 'districts'"
                  title="Sales by District"
                  :items="insights.district_sales_report || []"
                  :columns="districtSalesColumns"
                  :loading="false"
                  :default-sort="{ column: 'sales_amount', direction: 'desc' }"
                  empty-text="No district sales in this period."
                />

                <!-- PRODUCT SALES BY DISTRICT -->
                <BaseTable
                  v-else-if="drillTab === 'district_products'"
                  title="Product Sales by District"
                  :items="districtProductRows"
                  :columns="districtProductColumns"
                  :loading="false"
                  :sticky-first-column="true"
                  :default-sort="{ column: 'total', direction: 'desc' }"
                  empty-text="No product sales by district in this period."
                />
              </div>
            </template>

            <p v-else-if="drillError" class="bsr-empty">{{ drillError }}</p>
          </div>
        </aside>
      </div>
    </transition>

    <!-- ============================================================ -->
    <!-- LEADERBOARD "VIEW ALL" LIST PANE                             -->
    <!-- ============================================================ -->
    <transition name="bsr-slide">
      <div v-if="listPane" class="bsr-overlay bsr-overlay--list" role="dialog" :aria-label="`${listPane.title} — full list`">
        <div class="bsr-drill__backdrop" @click="closeListPane"></div>
        <aside class="bsr-drill__panel">
          <header class="bsr-drill__head">
            <span class="bsr-drill__avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            </span>
            <div class="bsr-drill__head-text">
              <span class="bsr-drill__eyebrow">Full list</span>
              <h2>{{ listPane.title }}</h2>
              <span class="bsr-drill__sub">
                {{ formatNumber(listCount) }} row{{ listCount === 1 ? '' : 's' }}
                <span v-if="rangeLabel"> · {{ rangeLabel }}</span>
              </span>
            </div>
            <button type="button" class="bsr-drill__close" aria-label="Close" @click="closeListPane">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>
          <div class="bsr-drill__body">
            <div v-if="listLoading" class="bsr-drill__loading">
              <div v-for="n in 4" :key="`lsk-${n}`" class="bsr-skeleton-tile"></div>
            </div>
            <p v-else-if="listError" class="bsr-empty">{{ listError }}</p>
            <BaseTable
              v-else
              :title="null"
              :items="listRows"
              :columns="listColumns"
              :loading="false"
              :paginated="listRows.length > 25"
              :page-size="25"
              :default-sort="{ column: 'sales', direction: 'desc' }"
              :row-class-fn="() => 'bsr-row-clickable'"
              empty-text="No rows in this period."
              @row-click="onListRowClick"
            >
              <template #cell-org_ids="{ row }">
                <span class="bsr-rank__orgs">
                  <span
                    v-for="oid in (row.org_ids || [])"
                    :key="`lp-org-${row.phone}-${oid}`"
                    class="bsr-org-badge"
                    :title="orgNameById(oid)"
                  >{{ orgShortById(oid) }}</span>
                </span>
              </template>
              <template #cell-sales="{ row }">
                <strong>{{ formatCurrency(row.sales) }}</strong>
              </template>
            </BaseTable>
          </div>
        </aside>
      </div>
    </transition>

    <!-- ============================================================ -->
    <!-- ROW ENTITY DETAIL PANE (customer / staff / product / district)-->
    <!-- ============================================================ -->
    <transition name="bsr-slide">
      <div v-if="entityDetail" class="bsr-overlay bsr-overlay--entity" role="dialog" :aria-label="`${entityHeaderTitle} details`">
        <div class="bsr-drill__backdrop" @click="closeEntityDetail"></div>
        <aside class="bsr-drill__panel">
          <header class="bsr-drill__head">
            <span class="bsr-drill__avatar" aria-hidden="true">
              <span class="bsr-drill__avatar-initial">{{ branchInitial(entityHeaderTitle) }}</span>
            </span>
            <div class="bsr-drill__head-text">
              <span class="bsr-drill__eyebrow">{{ entityTypeLabel }}</span>
              <h2>{{ entityHeaderTitle }}</h2>
              <span class="bsr-drill__sub">
                <template v-if="entityData && entityData.subtitle">{{ entityData.subtitle }}</template>
                <span v-if="rangeLabel"><template v-if="entityData && entityData.subtitle"> · </template>{{ rangeLabel }}</span>
              </span>
            </div>
            <button type="button" class="bsr-drill__close" aria-label="Close" @click="closeEntityDetail">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>
          <div class="bsr-drill__body">
            <div v-if="entityLoading" class="bsr-drill__loading">
              <div v-for="n in 4" :key="`esk-${n}`" class="bsr-skeleton-tile"></div>
            </div>
            <p v-else-if="entityError" class="bsr-empty">{{ entityError }}</p>
            <template v-else-if="entityData">
              <div class="bsr-drill__kpis">
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Sales</span>
                    <strong class="bsr-kpi__value">{{ formatCurrency(entityData.totals.sales) }}</strong>
                    <span class="bsr-kpi__sub">{{ formatNumber(entityData.totals.orders) }} orders</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true"><span class="bsr-kpi__glyph">₹</span></span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Orders</span>
                    <strong class="bsr-kpi__value">{{ formatNumber(entityData.totals.orders) }}</strong>
                    <span class="bsr-kpi__sub">in this range</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                  </span>
                </div>
                <div class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">{{ entityDetail.type === 'product' ? 'Units' : 'Items' }}</span>
                    <strong class="bsr-kpi__value">{{ formatNumber(entityData.totals.items) }}</strong>
                    <span class="bsr-kpi__sub">{{ entityDetail.type === 'product' ? 'quantity sold' : 'units sold' }}</span>
                  </div>
                  <span class="bsr-kpi__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  </span>
                </div>
                <div v-if="entityDetail.type !== 'product'" class="bsr-kpi bsr-kpi--compact">
                  <div class="bsr-kpi__text">
                    <span class="bsr-kpi__label">Returns</span>
                    <strong class="bsr-kpi__value bsr-kpi__value--warn">{{ formatNumber(entityData.totals.returns) }}</strong>
                    <span class="bsr-kpi__sub">returned orders</span>
                  </div>
                  <span class="bsr-kpi__icon bsr-kpi__icon--warn" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                  </span>
                </div>
              </div>

              <div v-if="entityData.org_breakdown && entityData.org_breakdown.length" class="bsr-drill__orgs">
                <div
                  v-for="org in entityData.org_breakdown"
                  :key="`edorg-${org.id}`"
                  class="bsr-orgstat"
                  :class="{ 'is-zero': !org.sales }"
                  :title="`${org.name}: ${formatCurrency(org.sales)} · ${formatNumber(org.orders)} orders`"
                >
                  <span class="bsr-orgstat__code">{{ shortOrgName(org.name) }}</span>
                  <strong class="bsr-orgstat__value">{{ formatCurrency(org.sales) }}</strong>
                  <span class="bsr-orgstat__sub">{{ formatNumber(org.orders) }} order{{ org.orders === 1 ? '' : 's' }}</span>
                </div>
              </div>

              <div v-if="entityData.breakdowns && entityData.breakdowns.length" class="bsr-entity__breakdowns">
                <section v-for="bd in entityData.breakdowns" :key="`bd-${bd.key}`" class="bsr-section bsr-leaderboard">
                  <header class="bsr-section__head">
                    <h2>{{ bd.title }}</h2>
                    <small class="bsr-section__hint">{{ bd.hint }}</small>
                  </header>
                  <ol v-if="bd.rows.length" class="bsr-rank">
                    <li v-for="(r, i) in bd.rows" :key="`bd-${bd.key}-${i}`" class="bsr-rank__item bsr-rank__item--static">
                      <span class="bsr-rank__pos">{{ i + 1 }}</span>
                      <div class="bsr-rank__body">
                        <span class="bsr-rank__name">{{ r.label }}</span>
                        <span class="bsr-rank__sub">{{ r.sub }}</span>
                      </div>
                      <strong class="bsr-rank__value">{{ formatCurrency(r.sales) }}</strong>
                    </li>
                  </ol>
                  <p v-else class="bsr-empty bsr-empty--inline">No data.</p>
                </section>
              </div>

              <BaseTable
                title="Recent Orders"
                :items="entityData.recent_orders"
                :columns="entityOrderColumns"
                :loading="false"
                :default-sort="{ column: 'order_date', direction: 'desc' }"
                empty-text="No orders in this period."
              >
                <template #cell-order_date="{ row }">{{ orderDateCell(row) }}</template>
                <template #cell-org_name="{ row }"><span class="bsr-state-pill">{{ row.org_name || '—' }}</span></template>
                <template #cell-grand_total="{ row }"><strong>{{ formatCurrency(row.grand_total) }}</strong></template>
              </BaseTable>
            </template>
          </div>
        </aside>
      </div>
    </transition>
  </div>
</template>


<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import branchesApi from '@/api/branches.js'
import { CChartDoughnut, CChartBar } from '@coreui/vue-chartjs'

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
const NUM = new Intl.NumberFormat('en-IN')
// Hide the umbrella holding org column — only display the three transacting
// orgs (Drugs / Agropharma / Vet Pharma) the user actually books against.
const VISIBLE_ORG_IDS = [1, 2, 4]
// Shared categorical palette for the mix charts (payment method, order type).
// Brand green leads, then a spread of distinct, on-brand-adjacent hues.
const MIX_PALETTE = ['#16a07a', '#0e5b46', '#2563eb', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b']
// Semantic colours for fulfillment pipeline stages — green = out the door,
// red = returned/failed, amber = waiting, slate = unspecified. Falls back to
// MIX_PALETTE for any stage not listed here.
const FULFILLMENT_COLORS = {
  shipped: '#16a07a',
  mailed_to_courier: '#0ea5a3',
  ready_to_ship: '#22c55e',
  for_picking: '#84cc16',
  for_print: '#a3e635',
  pending: '#f59e0b',
  on_hold: '#f97316',
  manifest_failed: '#ef4444',
  returned: '#dc2626',
  unspecified: '#94a3b8'
}

export default {
  name: 'Analytics',
  components: { DateRangePicker, BaseTable, CChartDoughnut, CChartBar },
  data () {
    // Local date — `toISOString` is UTC, which can be yesterday for India
    // users browsing past 18:30 IST. Match the toIso() helper used by the
    // preset buttons so the initial "Today" range matches order_date which
    // is stored in local time.
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return {
      // Combined description + branch-rollout caveat surfaced via the topbar
      // (i) tooltip — keeps the header chrome to a single row instead of a
      // multi-line subtitle + a yellow banner.
      aboutText: 'Totals, branch & state breakdowns, and per-branch drill-down (staff, items, returns). Admin view — no org switching needed.\n\nIncludes only orders tagged with a branch. Historical orders that pre-date the branch rollout stay in organisation-level totals but are not shown here.',
      activeTab: 'overview',
      // 'table' | 'grid' — view mode for the By Branch / By State tabs.
      // Defaults to the richer grid view; persisted to localStorage so a
      // user who explicitly flips back to the dense table view keeps that
      // choice across reloads.
      viewMode: (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('bsr.viewMode') === 'table') ? 'table' : 'grid',
      fromDate: today,
      toDate: today,
      organisations: [],
      // IDs of orgs whose logo URL failed to load — keyed so the template
      // swaps to the letter-avatar fallback instead of showing a broken
      // <img>. Cleared when the page remounts (per-conversation only).
      brokenLogos: {},
      branchRows: [],
      stateRows: [],
      overview: null,
      insights: null,
      drillBranch: null,
      drillTab: 'staff',
      drillLoading: false,
      // True while the active drill-down table is being exported to xlsx.
      drillExporting: false,
      drillError: '',
      // Free-text filter for the Sales By Person table (matches on user name).
      salesByPersonSearch: '',
      // "View all" list pane — full ranked list for one leaderboard metric.
      listPane: null,        // { metric, title } | null
      listRows: [],
      listCount: 0,
      listLoading: false,
      listError: '',
      // Per-row entity detail pane (customer / staff / product / district).
      entityDetail: null,    // { type, key, title } | null
      entityData: null,
      entityLoading: false,
      entityError: '',
      isLoading: false,
      errorMessage: '',
      loadedTabs: { overview: false, branch: false, state: false },
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'last-month', label: 'Last Month' }
      ],
      tabs: [
        { key: 'overview', label: 'Overview' },
        { key: 'branch', label: 'By Branch' },
        { key: 'state', label: 'By State' }
      ]
    }
  },
  computed: {
    orgRowKey () {
      return this.activeTab === 'branch' ? 'branch_id' : 'state_id'
    },
    dateRange: {
      get () { return { fromDate: this.fromDate, toDate: this.toDate } },
      set (value) {
        const next = {
          fromDate: (value && value.fromDate) || '',
          toDate: (value && value.toDate) || ''
        }
        if (next.fromDate !== this.fromDate || next.toDate !== this.toDate) {
          this.fromDate = next.fromDate
          this.toDate = next.toDate
          this.invalidateCaches()
        }
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    },
    activeColumns () {
      const orgCols = this.organisations.map((org) => ({
        key: `org_${org.id}`,
        label: org.name,
        align: 'right',
        width: '150px',
        sortable: true,
        // Sort numerically using the underlying sales field rather than the
        // formatted currency string slot output.
        formatter: (_, row) => row && row[`org_${org.id}_sales`] != null ? row[`org_${org.id}_sales`] : 0
      }))

      if (this.activeTab === 'branch') {
        return [
          { key: 'branch_name', label: 'Branch' },
          { key: 'state_name', label: 'State', width: '160px' },
          ...orgCols,
          { key: 'total_orders', label: 'Orders', align: 'right', format: 'number', width: '110px' },
          { key: 'total_sales', label: 'Total Sales', align: 'right', width: '160px' }
        ]
      }
      return [
        { key: 'state_name', label: 'State' },
        { key: 'branch_count', label: 'Branches', align: 'right', format: 'number', width: '110px' },
        ...orgCols,
        { key: 'total_orders', label: 'Orders', align: 'right', format: 'number', width: '110px' },
        { key: 'total_sales', label: 'Total Sales', align: 'right', width: '160px' }
      ]
    },
    tableRows () {
      const source = this.activeTab === 'branch' ? this.branchRows : this.stateRows
      const rows = source.map((row) => {
        const flat = { ...row }
        const byOrg = row.by_org || {}
        let totalSales = 0
        let totalOrders = 0
        this.organisations.forEach((org) => {
          const o = byOrg[String(org.id)] || byOrg[org.id] || { orders: 0, sales: 0, items: 0 }
          const sales = Number(o.sales) || 0
          const orders = Number(o.orders) || 0
          flat[`org_${org.id}_sales`] = sales
          flat[`org_${org.id}_orders`] = orders
          totalSales += sales
          totalOrders += orders
        })
        // Totals are summed across the visible orgs only so they line up with
        // the per-org columns — the backend's row.total_sales would include
        // every org and not match what's shown.
        flat.total_sales = totalSales
        flat.total_orders = totalOrders
        return flat
      })
      // Sort by total_sales desc so the grid view leads with the highest
      // sellers. BaseTable applies its own default-sort on top of this so
      // the table view is unaffected (and remains user-sortable).
      return rows.sort((a, b) => (Number(b.total_sales) || 0) - (Number(a.total_sales) || 0))
    },
    grandTotalOrders () {
      return this.tableRows.reduce((s, r) => s + (Number(r.total_orders) || 0), 0)
    },
    grandTotalSales () {
      return this.tableRows.reduce((s, r) => s + (Number(r.total_sales) || 0), 0)
    },
    emptyText () {
      if (this.activeTab === 'branch') {
        return 'No branches with sales in this period — once users are tagged to branches and new orders carry branch_id, rows will populate here.'
      }
      return 'No states with sales in this period.'
    },
    // Org ids the branch transacts in — drives which per-org columns
    // (kgs for Agro/org 1, farm bottles for Vet/org 4) the tables show,
    // mirroring Dashboard.vue's per-org column gating.
    branchOrgIds () {
      return new Set((((this.insights && this.insights.active_org_ids) || [])).map(Number))
    },
    // Sales By Person — clones Dashboard.vue's column set. kgs (org 1) and
    // farm-bottle (org 4) trios appear only when the branch sells them.
    salesByPersonColumns () {
      const orgIds = this.branchOrgIds
      const cols = [
        // Naming contract mirrors Dashboard.vue's sales tab exactly — see the
        // block there for the full rationale. In short: Billed = gross incl.
        // handed-off, C/O = net care-of adjustment, "… Sold" = Billed + C/O,
        // "Return …" = returns handled this period (same name on the Staff
        // Returns tab so cross-checking is name-to-name), "… After Returns" =
        // Sold − Return. Every money column states its shipping convention.
        { key: 'user', label: 'User' },
        { key: 'calls', label: 'Calls', align: 'right', format: 'number' },
        { key: 'order', label: 'Billed Orders', align: 'right', format: 'number' },
        { key: 'co_order', label: 'C/O Orders', align: 'right', format: 'number' },
        { key: 'total_orders', label: 'Orders', align: 'right', format: 'number' },
        { key: 'bottles', label: 'Billed Bottles', align: 'right', format: 'number' },
        { key: 'co_bottles', label: 'C/O Bottles', align: 'right', format: 'number' },
        { key: 'total_bottles', label: 'Bottles Sold', align: 'right', format: 'number' },
        { key: 'returned_bottles', label: 'Return Bottles', align: 'right', format: 'number' },
        { key: 'net_bottles', label: 'Bottles After Returns', align: 'right', format: 'number' }
      ]
      if (orgIds.has(4)) {
        cols.push(
          { key: 'farm_bottles', label: 'Billed Farm Bottles', align: 'right', format: 'number' },
          { key: 'co_farm_bottles', label: 'C/O Farm Bottles', align: 'right', format: 'number' },
          { key: 'total_farm_bottles', label: 'Farm Bottles Sold', align: 'right', format: 'number' },
          { key: 'returned_farm_bottles', label: 'Return Farm Bottles', align: 'right', format: 'number' },
          { key: 'net_farm_bottles', label: 'Farm Bottles After Returns', align: 'right', format: 'number' }
        )
      }
      if (orgIds.has(1)) {
        cols.push(
          { key: 'kgs_sold', label: 'Billed Kgs', align: 'right' },
          { key: 'co_kgs_sold', label: 'C/O Kgs', align: 'right' },
          { key: 'total_kgs_sold', label: 'Kgs Sold', align: 'right' },
          // Kg returns net against KG, never against bottles.
          { key: 'returned_kgs', label: 'Return Kgs', align: 'right' },
          { key: 'net_kgs', label: 'Kgs After Returns', align: 'right' }
        )
      }
      cols.push(
        // Money. Shipping is its own group and the Amount group is entirely
        // EXCLUDING shipping — every header says so. The Billed/C-O gap is
        // what flags a biller-role account.
        { key: 'shipping_amount', label: 'Billed Shipping', align: 'right', format: 'currency' },
        { key: 'co_shipping_amount', label: 'C/O Shipping', align: 'right', format: 'currency' },
        { key: 'total_shipping_cost', label: 'Shipping', align: 'right', format: 'currency' },
        { key: 'returned_shipping_amount', label: 'Return Shipping', align: 'right', format: 'currency' },
        { key: 'net_shipping_amount', label: 'Shipping After Returns', align: 'right', format: 'currency' },
        { key: 'sales_ex_shipping', label: 'Billed Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'co_sales_ex_shipping', label: 'C/O Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'total_amount_excl_shipping', label: 'Amount Sold (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'returned_amount_ex_shipping', label: 'Return Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'net_amount_ex_shipping', label: 'Amount After Returns (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'conversion', label: 'Conversion', align: 'right' }
      )
      return cols
    },
    itemwiseColumns () {
      return [
        { key: 'product_id', label: 'Product' },
        { key: 'quantity', label: 'Quantity', align: 'right', format: 'number', width: '150px' }
      ]
    },
    itemReturnReportColumns () {
      return [
        { key: 'product', label: 'Product' },
        { key: 'sold_quantity', label: 'Sold Qty', align: 'right', format: 'number', width: '110px' },
        { key: 'returned_quantity', label: 'Returned Qty', align: 'right', format: 'number', width: '120px' },
        { key: 'return_rate', label: 'Return Rate', align: 'right', format: 'percent', width: '110px' },
        { key: 'return_orders', label: 'Orders', align: 'right', format: 'number', width: '100px' },
        { key: 'return_amount', label: 'Return Amount', align: 'right', format: 'currency', width: '150px' }
      ]
    },
    processedReturnsColumns () {
      return [
        { key: 'sl_no', label: 'Sl. No', align: 'right', sortable: false, width: '70px' },
        { key: 'order_number', label: 'Order #' },
        { key: 'customer', label: 'Customer' },
        { key: 'products', label: 'Products', sortable: false },
        { key: 'staff', label: 'Staff' },
        { key: 'return_date', label: 'Return Date', format: 'date', width: '120px' },
        { key: 'return_amount', label: 'Amount', align: 'right', format: 'currency', width: '130px' },
        { key: 'credit_note', label: 'Credit Note' }
      ]
    },
    staffReturnReportColumns () {
      const orgIds = this.branchOrgIds
      const cols = [
        // "Return Bottles" / "Return Kgs" match the Sales By Person column
        // names exactly so a CA cross-checks name-to-name. Own-only and
        // c/o-only figures stay, but labelled as such.
        { key: 'user', label: 'Staff' },
        // Own-billed only, unlike Return Bottles / Return Kgs which are
        // own + c/o — "Own …" keeps the naming contract honest.
        { key: 'returns', label: 'Own Return Orders', align: 'right', format: 'number', width: '90px' },
        { key: 'co_orders_returned', label: 'C/O Return Orders', align: 'right', format: 'number' },
        { key: 'co_bottles_returned', label: 'C/O Return Bottles', align: 'right', format: 'number' },
        { key: 'total_bottles_returned', label: 'Return Bottles', align: 'right', format: 'number' }
      ]
      if (orgIds.has(4)) {
        cols.push(
          { key: 'co_farm_bottles_returned', label: 'C/O Return Farm Bottles', align: 'right', format: 'number' },
          { key: 'total_farm_bottles_returned', label: 'Return Farm Bottles', align: 'right', format: 'number' }
        )
      }
      if (orgIds.has(1)) {
        cols.push(
          { key: 'kgs_returned', label: 'Own Return Kgs', align: 'right', format: 'number' },
          { key: 'co_kgs_returned', label: 'C/O Return Kgs', align: 'right', format: 'number' },
          { key: 'total_kgs_returned', label: 'Return Kgs', align: 'right', format: 'number' }
        )
      }
      cols.push(
        { key: 'products', label: 'Products Returned', sortable: false },
        // Return Shipping + Return Amount (Excl. Shipping) add back up to
        // Return Amount (Incl. Shipping). Previously two of these three used
        // opposite shipping conventions and only one said so in its name.
        { key: 'total_shipping_cost', label: 'Return Shipping', align: 'right', format: 'currency' },
        { key: 'total_amount_excl_shipping', label: 'Return Amount (Excl. Shipping)', align: 'right', format: 'currency' },
        { key: 'return_amount', label: 'Return Amount (Incl. Shipping)', align: 'right', format: 'currency' }
      )
      return cols
    },
    roleWiseColumns () {
      return [
        { key: 'role', label: 'Role' },
        { key: 'orders', label: 'Orders', align: 'right', format: 'number' },
        { key: 'co_orders', label: 'C/O Orders', align: 'right', format: 'number' },
        { key: 'total_orders', label: 'Total Orders', align: 'right', format: 'number' },
        { key: 'bottles', label: 'Bottles', align: 'right', format: 'number' },
        { key: 'sales_amount', label: 'Sales Amount', align: 'right', format: 'currency' },
        { key: 'returns', label: 'Returns', align: 'right', format: 'number' },
        { key: 'return_rate', label: 'Return Rate', align: 'right', format: 'percent' },
        { key: 'return_amount', label: 'Return Amount', align: 'right', format: 'currency' }
      ]
    },
    shippingReportColumns () {
      return [
        { key: 'courier', label: 'Courier' },
        { key: 'total_orders', label: 'Total Orders', align: 'right', format: 'number' },
        { key: 'pending', label: 'Pending', align: 'right', format: 'number' },
        { key: 'shipped', label: 'Shipped', align: 'right', format: 'number' },
        { key: 'returned', label: 'Returned', align: 'right', format: 'number' },
        { key: 'cancelled', label: 'Cancelled', align: 'right', format: 'number' },
        { key: 'fulfilment_rate', label: 'Fulfilment Rate', align: 'right', format: 'percent' }
      ]
    },
    districtSalesColumns () {
      return [
        { key: 'district', label: 'District' },
        { key: 'state', label: 'State' },
        { key: 'orders', label: 'Orders', align: 'right', format: 'number' },
        { key: 'bottles', label: 'Bottles', align: 'right', format: 'number' },
        { key: 'sales_amount', label: 'Sales Amount', align: 'right', format: 'currency' },
        { key: 'returns', label: 'Returns', align: 'right', format: 'number' },
        { key: 'return_rate', label: 'Return Rate', align: 'right', format: 'percent' },
        { key: 'return_amount', label: 'Return Amount', align: 'right', format: 'currency' }
      ]
    },
    staffProductRows () {
      return (this.insights && this.insights.staff_product_sales && this.insights.staff_product_sales.rows) || []
    },
    staffProductColumns () {
      const sp = (this.insights && this.insights.staff_product_sales) || { columns: [] }
      return [
        { key: 'user', label: 'Staff' },
        ...(sp.columns || []).map((c) => ({ key: c.key, label: c.label, align: 'right', format: 'number' })),
        { key: 'total', label: 'Total', align: 'right', format: 'number' }
      ]
    },
    districtProductRows () {
      return (this.insights && this.insights.district_product_sales && this.insights.district_product_sales.rows) || []
    },
    districtProductColumns () {
      const dp = (this.insights && this.insights.district_product_sales) || { columns: [] }
      return [
        { key: 'district', label: 'District' },
        ...(dp.columns || []).map((c) => ({ key: c.key, label: c.label, align: 'right', format: 'number' })),
        { key: 'total', label: 'Total', align: 'right', format: 'number' }
      ]
    },
    // Sales By Person rows narrowed by the toolbar search box (matches
    // against the user name, case-insensitive). Reused by both the table
    // and the Excel export so the two always agree on what's visible.
    filteredSalesByPerson () {
      const rows = (this.insights && this.insights.sales_by_person) || []
      const q = String(this.salesByPersonSearch || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter((r) => String(r.user || '').toLowerCase().includes(q))
    },
    // (title, columns, rows) for whichever drill tab is active — drives the
    // per-tab Export to Excel button.
    activeDrillTable () {
      const i = this.insights
      if (!i) return null
      const map = {
        itemwise: { title: 'Itemwise Report', columns: this.itemwiseColumns, rows: i.itemwise_report || [] },
        sales: { title: 'Sales By Person', columns: this.salesByPersonColumns, rows: this.filteredSalesByPerson },
        staff_products: { title: 'Staff Product Sales', columns: this.staffProductColumns, rows: this.staffProductRows },
        item_returns: { title: 'Item Return Report', columns: this.itemReturnReportColumns, rows: i.item_return_report || [] },
        returns: { title: 'Returns', columns: this.processedReturnsColumns, rows: i.processed_returns_report || [] },
        staff_returns: { title: 'Staff Returns', columns: this.staffReturnReportColumns, rows: i.staff_return_report || [] },
        roles: { title: 'Role-Wise Report', columns: this.roleWiseColumns, rows: i.role_wise_report || [] },
        shipping: { title: 'Shipping Report', columns: this.shippingReportColumns, rows: i.shipping_report || [] },
        districts: { title: 'Sales by District', columns: this.districtSalesColumns, rows: i.district_sales_report || [] },
        district_products: { title: 'Product Sales by District', columns: this.districtProductColumns, rows: this.districtProductRows }
      }
      return map[this.drillTab] || null
    },
    // Columns for the "View all" list pane, keyed off the active metric.
    listColumns () {
      const m = this.listPane ? this.listPane.metric : ''
      const orders = { key: 'orders', label: 'Orders', align: 'right', format: 'number', width: '100px' }
      const sales = { key: 'sales', label: 'Sales', align: 'right', width: '150px' }
      const custName = { key: 'name', label: 'Customer', formatter: (v) => v || 'Unknown' }
      if (m === 'branches' || m === 'store_branches') {
        return [
          { key: 'branch_name', label: 'Branch' },
          { key: 'state_name', label: 'State', width: '150px' },
          orders, sales
        ]
      }
      if (m === 'staff') {
        return [
          { key: 'user_name', label: 'Staff' },
          { key: 'branch_name', label: 'Branch', width: '150px' },
          orders,
          { key: 'items', label: 'Items', align: 'right', format: 'number', width: '90px' },
          { key: 'returns', label: 'Returns', align: 'right', format: 'number', width: '90px' },
          sales
        ]
      }
      if (m === 'products') {
        return [
          { key: 'name', label: 'Product' },
          { key: 'sku', label: 'SKU', width: '130px' },
          { key: 'quantity', label: 'Qty', align: 'right', format: 'number', width: '90px' },
          orders, sales
        ]
      }
      if (m === 'districts') {
        return [
          { key: 'district', label: 'District' },
          { key: 'state_name', label: 'State', width: '150px' },
          orders, sales
        ]
      }
      if (m === 'multi_org_customers') {
        return [
          custName,
          { key: 'phone', label: 'Phone', width: '140px' },
          { key: 'org_ids', label: 'Orgs', sortable: false, width: '150px' },
          orders, sales
        ]
      }
      // customers (frequent)
      return [
        custName,
        { key: 'phone', label: 'Phone', width: '140px' },
        { key: 'district', label: 'District', width: '140px' },
        orders, sales
      ]
    },
    entityTypeLabel () {
      const t = this.entityDetail ? this.entityDetail.type : ''
      return { customer: 'Customer', staff: 'Staff', product: 'Product', district: 'District' }[t] || 'Detail'
    },
    entityHeaderTitle () {
      if (this.entityData && this.entityData.title) return this.entityData.title
      return (this.entityDetail && this.entityDetail.title) || '—'
    },
    entityOrderColumns () {
      return [
        { key: 'order_number', label: 'Order', width: '160px' },
        { key: 'order_date', label: 'Date', width: '120px' },
        { key: 'contact_name', label: 'Customer' },
        { key: 'org_name', label: 'Org', width: '150px' },
        { key: 'grand_total', label: 'Value', align: 'right', width: '130px' }
      ]
    },
    // Drill-down tabs — clone the admin dashboard's report tables (minus
    // HMS Patients, org-2 only), scoped to this branch. Counts come off the
    // new dashboard-parity datasets returned by the insights endpoint.
    drillTabs () {
      const i = this.insights
      const len = (v) => (Array.isArray(v) ? v.length : 0)
      return [
        { key: 'itemwise', label: 'Itemwise', count: i ? len(i.itemwise_report) : null },
        { key: 'sales', label: 'Sales By Person', count: i ? len(i.sales_by_person) : null },
        { key: 'staff_products', label: 'Staff Product Sales', count: i ? len(i.staff_product_sales && i.staff_product_sales.rows) : null },
        { key: 'item_returns', label: 'Item Return Report', count: i ? len(i.item_return_report) : null },
        { key: 'returns', label: 'Returns', count: i ? len(i.processed_returns_report) : null },
        { key: 'staff_returns', label: 'Staff Returns', count: i ? len(i.staff_return_report) : null },
        { key: 'roles', label: 'Role-Wise', count: i ? len(i.role_wise_report) : null },
        { key: 'shipping', label: 'Shipping', count: i ? len(i.shipping_report) : null },
        { key: 'districts', label: 'Sales by District', count: i ? len(i.district_sales_report) : null },
        { key: 'district_products', label: 'Product Sales by District', count: i ? len(i.district_product_sales && i.district_product_sales.rows) : null }
      ]
    },
    trendChartData () {
      if (!this.overview || !this.overview.trend.length) {
        return { labels: [], datasets: [] }
      }
      const trend = this.overview.trend
      // Hide line points on long ranges so the orders line stays readable.
      const pointRadius = trend.length > 45 ? 0 : 3
      return {
        labels: trend.map((d) => this.formatChartDate(d.date)),
        datasets: [
          {
            type: 'bar',
            label: 'Sales',
            yAxisID: 'sales',
            backgroundColor: 'rgba(22, 160, 122, 0.55)',
            hoverBackgroundColor: 'rgba(14, 91, 70, 0.85)',
            borderColor: 'transparent',
            maxBarThickness: 34,
            data: trend.map((d) => Math.round(d.sales))
          },
          {
            type: 'line',
            label: 'Orders',
            yAxisID: 'orders',
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
            borderColor: '#2563eb',
            pointBackgroundColor: '#1d4ed8',
            pointBorderColor: '#ffffff',
            pointRadius,
            pointHoverRadius: 4,
            borderWidth: 2.5,
            tension: 0.35,
            fill: false,
            data: trend.map((d) => Number(d.orders) || 0)
          }
        ]
      }
    },
    trendChartOptions () {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: { fontColor: '#475569', boxWidth: 12, padding: 14, usePointStyle: true, fontSize: 11 }
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#64748b', maxTicksLimit: 12 } }],
          yAxes: [
            {
              id: 'sales',
              position: 'left',
              ticks: { beginAtZero: true, fontColor: '#64748b', callback: (v) => this.formatCurrencyCompact(v) },
              gridLines: { color: '#eef1f7' },
              scaleLabel: { display: true, labelString: 'Sales', fontColor: '#94a3b8', fontSize: 10 }
            },
            {
              id: 'orders',
              position: 'right',
              ticks: { beginAtZero: true, precision: 0, fontColor: '#64748b' },
              gridLines: { display: false },
              scaleLabel: { display: true, labelString: 'Orders', fontColor: '#94a3b8', fontSize: 10 }
            }
          ]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, data) => {
              const ds = data.datasets[item.datasetIndex] || {}
              if (ds.yAxisID === 'orders') return `Orders: ${NUM.format(item.yLabel || 0)}`
              return `Sales: ${INR.format(item.yLabel || 0)}`
            }
          }
        }
      }
    },
    paymentMixChart () {
      const mix = (this.overview && this.overview.payment_mix) || []
      if (!mix.length) return { labels: [], datasets: [] }
      return {
        labels: mix.map((m) => this.formatMixLabel(m.method)),
        datasets: [
          {
            backgroundColor: mix.map((_, i) => MIX_PALETTE[i % MIX_PALETTE.length]),
            borderColor: '#ffffff',
            borderWidth: 2,
            data: mix.map((m) => Math.round(m.sales))
          }
        ]
      }
    },
    doughnutChartOptions () {
      const mix = (this.overview && this.overview.payment_mix) || []
      const total = mix.reduce((s, m) => s + (Number(m.sales) || 0), 0)
      return {
        maintainAspectRatio: false,
        responsive: true,
        cutoutPercentage: 62,
        legend: {
          position: 'right',
          labels: { fontColor: '#475569', boxWidth: 12, padding: 12, fontSize: 11 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const value = Number(data.datasets[0].data[item.index]) || 0
              const pct = total > 0 ? Math.round((value / total) * 100) : 0
              return `${data.labels[item.index]}: ${INR.format(value)} (${pct}%)`
            }
          }
        }
      }
    },
    // Top Sales by Mode — the order-type mix (Amazon / Telesales / Direct /
    // Flipkart / Website / …) ranked by sales, whole-org (not scoped to any
    // staff or customer). Backend already returns order_type_mix sorted by
    // sales desc; we re-sort defensively and pre-format the labels.
    topSalesByMode () {
      const mix = (this.overview && this.overview.order_type_mix) || []
      return mix
        .map((m) => ({
          key: m.order_type,
          label: this.formatMixLabel(m.order_type),
          orders: Number(m.orders) || 0,
          sales: Number(m.sales) || 0
        }))
        .sort((a, b) => b.sales - a.sales)
    },
    orderTypeChart () {
      const mix = (this.overview && this.overview.order_type_mix) || []
      if (!mix.length) return { labels: [], datasets: [] }
      return {
        labels: mix.map((m) => this.formatMixLabel(m.order_type)),
        datasets: [
          {
            label: 'Sales',
            backgroundColor: mix.map((_, i) => MIX_PALETTE[i % MIX_PALETTE.length]),
            maxBarThickness: 54,
            data: mix.map((m) => Math.round(m.sales))
          }
        ]
      }
    },
    barChartOptions () {
      const mix = (this.overview && this.overview.order_type_mix) || []
      const orderCounts = mix.map((m) => Number(m.orders) || 0)
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#64748b' } }],
          yAxes: [{ ticks: { beginAtZero: true, fontColor: '#64748b' }, gridLines: { color: '#eef1f7' } }]
        },
        tooltips: {
          callbacks: {
            label: (item) => {
              const orders = orderCounts[item.index] || 0
              return `${INR.format(item.yLabel || 0)} · ${NUM.format(orders)} orders`
            }
          }
        }
      }
    },
    callTypeChart () {
      const mix = (this.overview && this.overview.call_type_mix) || []
      if (!mix.length) return { labels: [], datasets: [] }
      return {
        labels: mix.map((m) => this.formatMixLabel(m.call_type)),
        datasets: [
          {
            label: 'Calls',
            backgroundColor: mix.map((_, i) => MIX_PALETTE[i % MIX_PALETTE.length]),
            maxBarThickness: 54,
            data: mix.map((m) => Number(m.calls) || 0)
          }
        ]
      }
    },
    callBarChartOptions () {
      const mix = (this.overview && this.overview.call_type_mix) || []
      const durations = mix.map((m) => Number(m.duration) || 0)
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#64748b' } }],
          yAxes: [{ ticks: { beginAtZero: true, fontColor: '#64748b', precision: 0 }, gridLines: { color: '#eef1f7' } }]
        },
        tooltips: {
          callbacks: {
            label: (item) => {
              const dur = durations[item.index] || 0
              return `${NUM.format(item.yLabel || 0)} calls · ${this.formatDuration(dur)} total`
            }
          }
        }
      }
    },
    fulfillmentChart () {
      const mix = (this.overview && this.overview.fulfillment_mix) || []
      if (!mix.length) return { labels: [], datasets: [] }
      return {
        labels: mix.map((m) => this.formatMixLabel(m.status)),
        datasets: [
          {
            backgroundColor: mix.map((m, i) => FULFILLMENT_COLORS[m.status] || MIX_PALETTE[i % MIX_PALETTE.length]),
            borderColor: '#ffffff',
            borderWidth: 2,
            data: mix.map((m) => Number(m.orders) || 0)
          }
        ]
      }
    },
    fulfillmentChartOptions () {
      const mix = (this.overview && this.overview.fulfillment_mix) || []
      const total = mix.reduce((s, m) => s + (Number(m.orders) || 0), 0)
      const sales = mix.map((m) => Number(m.sales) || 0)
      return {
        maintainAspectRatio: false,
        responsive: true,
        cutoutPercentage: 62,
        legend: {
          position: 'right',
          labels: { fontColor: '#475569', boxWidth: 12, padding: 10, fontSize: 11 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const value = Number(data.datasets[0].data[item.index]) || 0
              const pct = total > 0 ? Math.round((value / total) * 100) : 0
              return `${data.labels[item.index]}: ${NUM.format(value)} orders (${pct}%) · ${INR.format(sales[item.index] || 0)}`
            }
          }
        }
      }
    },
    retentionTotals () {
      const r = (this.overview && this.overview.retention) || {}
      const newC = (r.new && r.new.customers) || 0
      const repeatC = (r.repeat && r.repeat.customers) || 0
      const customers = newC + repeatC
      return {
        customers,
        newCustomers: newC,
        repeatCustomers: repeatC,
        repeatRate: customers > 0 ? (repeatC / customers) * 100 : 0
      }
    },
    retentionChart () {
      const r = (this.overview && this.overview.retention) || {}
      const newO = (r.new && r.new.orders) || 0
      const repeatO = (r.repeat && r.repeat.orders) || 0
      if (!newO && !repeatO) return { labels: [], datasets: [] }
      return {
        labels: ['New', 'Repeat'],
        datasets: [
          {
            backgroundColor: ['#2563eb', '#16a07a'],
            borderColor: '#ffffff',
            borderWidth: 2,
            data: [newO, repeatO]
          }
        ]
      }
    },
    retentionChartOptions () {
      const r = (this.overview && this.overview.retention) || {}
      const customers = [(r.new && r.new.customers) || 0, (r.repeat && r.repeat.customers) || 0]
      const sales = [(r.new && r.new.sales) || 0, (r.repeat && r.repeat.sales) || 0]
      const orders = [(r.new && r.new.orders) || 0, (r.repeat && r.repeat.orders) || 0]
      const totalOrders = orders[0] + orders[1]
      return {
        maintainAspectRatio: false,
        responsive: true,
        cutoutPercentage: 62,
        legend: {
          position: 'right',
          labels: { fontColor: '#475569', boxWidth: 12, padding: 12, fontSize: 11 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const o = orders[item.index] || 0
              const pct = totalOrders > 0 ? Math.round((o / totalOrders) * 100) : 0
              return `${data.labels[item.index]}: ${NUM.format(customers[item.index] || 0)} customers · ${NUM.format(o)} orders (${pct}%) · ${INR.format(sales[item.index] || 0)}`
            }
          }
        }
      }
    }
  },
  created () {
    this.loadActiveTab()
  },
  mounted () {
    document.addEventListener('keydown', this.onKeyDown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeyDown)
  },
  methods: {
    setTab (key) {
      if (this.activeTab === key) return
      this.activeTab = key
      this.loadActiveTab()
    },
    invalidateCaches () {
      this.loadedTabs = { overview: false, branch: false, state: false }
      // Drill-down + open panes are also stale once the date range moves.
      if (this.drillBranch) {
        this.loadInsights(this.drillBranch.branch_id)
      }
      if (this.listPane) this.loadList()
      if (this.entityDetail) this.loadEntityDetail()
    },
    loadActiveTab () {
      if (this.activeTab === 'overview') {
        if (this.loadedTabs.overview && this.overview) return
        this.loadOverview()
      } else if (this.activeTab === 'branch') {
        if (this.loadedTabs.branch && this.branchRows.length) return
        this.loadByBranch()
      } else {
        if (this.loadedTabs.state && this.stateRows.length) return
        this.loadByState()
      }
    },
    displayOrganisations (orgs) {
      return (orgs || []).filter((o) => VISIBLE_ORG_IDS.includes(Number(o.id)))
    },
    applyResponse (data, target) {
      const orgs = Array.isArray(data?.organisations) ? data.organisations : []
      this.organisations = this.displayOrganisations(orgs)
      const rows = Array.isArray(data?.rows) ? data.rows : []
      if (target === 'branch') this.branchRows = rows
      else this.stateRows = rows
    },
    overviewByOrg (orgId) {
      const fallback = { orders: 0, sales: 0, returns: 0 }
      if (!this.overview) return fallback
      const byOrg = this.overview.by_org || {}
      return byOrg[String(orgId)] || byOrg[orgId] || fallback
    },
    // Logos live on the Vuex `organisations` state (populated at login from
    // /api/organisations and normalised in store.js — see logo_url). The
    // overview endpoint only returns id + name to keep its payload lean,
    // so we cross-reference here. Returns '' when no logo or the image
    // previously failed to load (handled by onLogoError).
    orgLogo (orgId) {
      if (this.brokenLogos[orgId]) return ''
      const list = (this.$store && this.$store.state && this.$store.state.organisations) || []
      const match = list.find((o) => Number(o.id) === Number(orgId))
      return match && match.logo_url ? match.logo_url : ''
    },
    orgInitial (name) {
      if (!name) return '?'
      const trimmed = String(name).trim()
      return trimmed.length ? trimmed.charAt(0).toUpperCase() : '?'
    },
    onLogoError (orgId) {
      // Vue 2 reactivity — direct assignment to a non-declared key won't
      // re-render, so use $set.
      this.$set(this.brokenLogos, orgId, true)
    },
    loadOverview () {
      this.errorMessage = ''
      this.isLoading = true
      branchesApi.overview(this.$apiAdress, {
        fromDate: this.fromDate,
        toDate: this.toDate,
        organisation_ids: VISIBLE_ORG_IDS
      })
        .then((data) => {
          this.overview = data
          this.organisations = this.displayOrganisations(data?.organisations || [])
          this.loadedTabs.overview = true
        })
        .catch((error) => {
          this.overview = null
          this.errorMessage = error?.response?.data?.message || 'Unable to load overview.'
        })
        .finally(() => { this.isLoading = false })
    },
    loadByBranch () {
      this.errorMessage = ''
      this.isLoading = true
      branchesApi.salesByBranch(this.$apiAdress, { fromDate: this.fromDate, toDate: this.toDate })
        .then((data) => {
          this.applyResponse(data, 'branch')
          this.loadedTabs.branch = true
        })
        .catch((error) => {
          this.branchRows = []
          this.errorMessage = error?.response?.data?.message || 'Unable to load branch sales.'
        })
        .finally(() => { this.isLoading = false })
    },
    loadByState () {
      this.errorMessage = ''
      this.isLoading = true
      branchesApi.salesByState(this.$apiAdress, { fromDate: this.fromDate, toDate: this.toDate })
        .then((data) => {
          this.applyResponse(data, 'state')
          this.loadedTabs.state = true
        })
        .catch((error) => {
          this.stateRows = []
          this.errorMessage = error?.response?.data?.message || 'Unable to load state sales.'
        })
        .finally(() => { this.isLoading = false })
    },
    onRowClick (row) {
      if (this.activeTab !== 'branch' || !row || !row.branch_id) return
      this.openBranchDrillDown(row)
    },
    openBranchDrillDown (row) {
      this.drillBranch = {
        branch_id: row.branch_id,
        branch_name: row.branch_name,
        state_name: row.state_name,
        image_url: row.image_url || null
      }
      this.drillTab = 'sales'
      this.salesByPersonSearch = ''
      this.loadInsights(row.branch_id)
    },
    closeBranchDrillDown () {
      this.drillBranch = null
      this.insights = null
      this.salesByPersonSearch = ''
      this.drillError = ''
    },
    loadInsights (branchId) {
      this.drillLoading = true
      this.drillError = ''
      this.insights = null
      branchesApi.insights(this.$apiAdress, branchId, {
        fromDate: this.fromDate,
        toDate: this.toDate,
        organisation_ids: VISIBLE_ORG_IDS
      })
        .then((data) => {
          // Decorate Sales By Person rows with the own+c/o totals the table
          // columns bind to (same derivation as Dashboard.vue).
          if (data && Array.isArray(data.sales_by_person)) {
            data.sales_by_person = data.sales_by_person.map((row) => ({
              ...row,
              total_orders: Number(row.order || 0) + Number(row.co_order || 0),
              total_bottles: Number(row.bottles || 0) + Number(row.co_bottles || 0),
              total_kgs_sold: Number(row.kgs_sold || 0) + Number(row.co_kgs_sold || 0),
              total_farm_bottles: Number(row.farm_bottles || 0) + Number(row.co_farm_bottles || 0)
            }))
          }
          this.insights = data
        })
        .catch((error) => {
          this.drillError = error?.response?.data?.message || 'Unable to load branch insights.'
        })
        .finally(() => { this.drillLoading = false })
    },
    onKeyDown (event) {
      if (event.key !== 'Escape') return
      // Close the top-most pane first (drill > entity > list by z-index).
      if (this.drillBranch) { this.closeBranchDrillDown(); return }
      if (this.entityDetail) { this.closeEntityDetail(); return }
      if (this.listPane) { this.closeListPane() }
    },
    // ---- "View all" list pane ----
    openListPane (metric, title) {
      this.listPane = { metric, title }
      this.listRows = []
      this.listCount = 0
      this.loadList()
    },
    closeListPane () {
      this.listPane = null
      this.listRows = []
      this.listError = ''
    },
    loadList () {
      if (!this.listPane) return
      this.listLoading = true
      this.listError = ''
      branchesApi.leaderboard(this.$apiAdress, {
        metric: this.listPane.metric,
        fromDate: this.fromDate,
        toDate: this.toDate,
        organisation_ids: VISIBLE_ORG_IDS
      })
        .then((data) => {
          this.listRows = (data && data.rows) || []
          this.listCount = (data && data.count != null) ? data.count : this.listRows.length
        })
        .catch((error) => {
          this.listRows = []
          this.listError = error?.response?.data?.message || 'Unable to load list.'
        })
        .finally(() => { this.listLoading = false })
    },
    onListRowClick (row) {
      if (this.listPane) this.rowDetail(this.listPane.metric, row)
    },
    // Route a leaderboard row to the right detail surface: branch rows reuse
    // the existing branch drill-down; everything else opens the entity pane.
    rowDetail (metric, row) {
      if (!row) return
      if (metric === 'branches' || metric === 'store_branches') {
        this.openBranchDrillDown({
          branch_id: row.branch_id,
          branch_name: row.branch_name,
          state_name: row.state_name,
          image_url: row.image_url
        })
      } else if (metric === 'staff') {
        this.openEntityDetail('staff', row.user_id, row.user_name)
      } else if (metric === 'products') {
        this.openEntityDetail('product', row.item_id != null ? row.item_id : row.name, row.name)
      } else if (metric === 'districts') {
        this.openEntityDetail('district', row.district, row.district)
      } else {
        // customers + multi_org_customers
        this.openEntityDetail('customer', row.phone, row.name || 'Unknown')
      }
    },
    // ---- Per-row entity detail pane ----
    openEntityDetail (type, key, title) {
      this.entityDetail = { type, key, title }
      this.entityData = null
      this.loadEntityDetail()
    },
    closeEntityDetail () {
      this.entityDetail = null
      this.entityData = null
      this.entityError = ''
    },
    loadEntityDetail () {
      if (!this.entityDetail) return
      this.entityLoading = true
      this.entityError = ''
      this.entityData = null
      branchesApi.entityDetail(this.$apiAdress, {
        type: this.entityDetail.type,
        key: this.entityDetail.key,
        // Name lets the backend disambiguate marketplace placeholder phones
        // (e.g. 0000000001, shared by every Amazon order) down to one buyer.
        name: this.entityDetail.title,
        fromDate: this.fromDate,
        toDate: this.toDate,
        organisation_ids: VISIBLE_ORG_IDS
      })
        .then((data) => { this.entityData = data })
        .catch((error) => {
          this.entityError = error?.response?.data?.message || 'Unable to load details.'
        })
        .finally(() => { this.entityLoading = false })
    },
    // Recent-order date cell — prefer order_date (proper datetime), fall back
    // to created_at (Unix seconds → ms) per the Order timestamp convention.
    orderDateCell (row) {
      if (row && row.order_date) return this.formatDate(row.order_date)
      const ts = Number(row && row.created_at)
      if (Number.isFinite(ts) && ts > 0) {
        const d = new Date(ts * 1000)
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
      }
      return '—'
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    formatDate (value) {
      if (!value) return '—'
      const iso = String(value).split('T')[0].split(' ')[0]
      return this.formatDisplay(iso)
    },
    formatChartDate (iso) {
      if (!iso) return ''
      const [, m, d] = iso.split('-')
      if (!m || !d) return iso
      return `${d}/${m}`
    },
    formatCurrency (n) {
      const value = Number(n) || 0
      return INR.format(value)
    },
    // Indian-numbering compact formatter for tight card spots (hero chip,
    // 3-column per-org row) where a 10-figure full value like
    // ₹1,23,45,67,890 would overflow. Output: ₹999 / ₹12K / ₹3.4L / ₹12Cr.
    // Always pair the call site with :title="formatCurrency(n)" so the
    // precise value is one hover away.
    formatCurrencyCompact (n) {
      const value = Number(n) || 0
      if (!value) return '₹0'
      const abs = Math.abs(value)
      const sign = value < 0 ? '-' : ''
      const fmt = (v, fractionDigits) => {
        const fixed = v.toFixed(fractionDigits)
        // Trim trailing zeros / dot so "12.00" reads as "12", not "12.00".
        return fixed.replace(/\.?0+$/, '')
      }
      if (abs >= 10000000) { // 1 crore
        const cr = abs / 10000000
        const digits = cr >= 100 ? 0 : cr >= 10 ? 1 : 2
        return `${sign}₹${fmt(cr, digits)}Cr`
      }
      if (abs >= 100000) { // 1 lakh
        const l = abs / 100000
        const digits = l >= 100 ? 0 : l >= 10 ? 1 : 2
        return `${sign}₹${fmt(l, digits)}L`
      }
      if (abs >= 1000) {
        const k = abs / 1000
        const digits = k >= 100 ? 0 : 1
        return `${sign}₹${fmt(k, digits)}K`
      }
      return INR.format(value)
    },
    formatNumber (n) {
      return NUM.format(Number(n) || 0)
    },
    formatPercent (n) {
      const value = Number(n) || 0
      return `${value.toFixed(value >= 10 ? 0 : 1)}%`
    },
    setViewMode (mode) {
      if (mode !== 'table' && mode !== 'grid') return
      if (this.viewMode === mode) return
      this.viewMode = mode
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('bsr.viewMode', mode)
        }
      } catch (e) { /* localStorage can throw in some private modes */ }
    },
    // Excel type for a drill-down table column. The backend workbook builder
    // supports text | number | decimal | percent, so currency/kgs map to
    // decimal and the conversion string maps to percent.
    drillColType (col) {
      if (col.key === 'conversion') return 'percent'
      if (col.format === 'currency') return 'decimal'
      if (col.format === 'percent') return 'percent'
      if (col.format === 'number') return 'number'
      if (/kgs/.test(col.key)) return 'decimal'
      return 'text'
    },
    // Raw export value for a drill-down cell: row number for sl_no, the parsed
    // number for the conversion string, a joined "name ×qty" list for product
    // arrays, else the plain field.
    drillCellValue (col, row, index) {
      if (col.key === 'sl_no') return index + 1
      if (col.key === 'conversion') {
        const match = String(row.conversion == null ? '' : row.conversion).match(/-?\d+(\.\d+)?/)
        return match ? Number(match[0]) : ''
      }
      const v = row[col.key]
      if (Array.isArray(v)) return v.map((p) => `${p.name} ×${p.quantity}`).join(', ')
      return v
    },
    // Export the active drill-down table to a styled .xlsx, reusing the
    // generic Sales-By-Person workbook builder on the backend.
    async exportDrillTable () {
      if (this.drillExporting) return
      const tbl = this.activeDrillTable
      if (!tbl || !tbl.rows.length) return

      const columns = tbl.columns.map((c) => ({ label: c.label, type: this.drillColType(c) }))
      const rows = tbl.rows.map((row, i) => tbl.columns.map((c) => this.drillCellValue(c, row, i)))
      const branchName = (this.drillBranch && this.drillBranch.branch_name) || 'Branch'
      const range = this.fromDate === this.toDate
        ? this.formatDisplay(this.fromDate)
        : `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
      const subtitle = `${branchName}  ·  ${range}  ·  ${rows.length} rows`
      const slug = tbl.title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

      this.drillExporting = true
      try {
        const response = await axios.post(
          `${this.$apiAdress}/api/dashboard/sales-by-person-xlsx`,
          { title: `${tbl.title} — ${branchName}`, subtitle, columns, rows },
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${slug}_${this.fromDate}_to_${this.toDate}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.drillError = `Unable to export ${tbl.title} right now.`
      } finally {
        this.drillExporting = false
      }
    },
    branchInitial (name) {
      if (!name) return '?'
      const trimmed = String(name).trim()
      return trimmed.length ? trimmed.charAt(0).toUpperCase() : '?'
    },
    // Deterministic hash → palette slot, so the same branch always gets the
    // same avatar gradient across loads. Keeps the grid visually anchored
    // when users scan it.
    branchAvatarTone (name) {
      const tones = ['green', 'teal', 'indigo', 'amber', 'slate', 'plum']
      const key = String(name || '')
      let hash = 0
      for (let i = 0; i < key.length; i++) {
        hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0
      }
      return tones[Math.abs(hash) % tones.length]
    },
    // Per-org chips on the cards are constrained columns — show a 3-letter
    // abbreviation built from each word's initial (Manakkal Agro Pharma →
    // "MAP", Manakkal Drugs India → "MDI", Manakkal Veterinary Pharma →
    // "MVP"). The chip's title attribute still carries the full name for
    // a hover tooltip.
    shortOrgName (name) {
      if (!name) return ''
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return ''
      return parts.map((p) => p.charAt(0)).join('').toUpperCase().slice(0, 4)
    },
    // Resolve an org id → full name from the loaded organisations list.
    // Used by the multi-org-customer badges; falls back to "Org N".
    orgNameById (id) {
      const match = this.organisations.find((o) => Number(o.id) === Number(id))
      return match ? match.name : `Org ${id}`
    },
    // Org id → short badge code (MAP / MDI / MVP) via shortOrgName.
    orgShortById (id) {
      const match = this.organisations.find((o) => Number(o.id) === Number(id))
      return match ? this.shortOrgName(match.name) : `O${id}`
    },
    // Tidy a raw enum-ish value (payment_method / order_type) into a chart
    // label. Common acronyms stay upper-case; everything else is title-cased.
    formatMixLabel (value) {
      if (!value) return 'Unspecified'
      const raw = String(value).trim()
      const upper = raw.toUpperCase()
      if (['COD', 'VPP', 'POS', 'UPI', 'NEFT', 'RTGS', 'B2B', 'B2C'].includes(upper)) return upper
      return raw
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    // Calls store `duration` in seconds. Render a compact "1h 12m" / "5m 30s"
    // / "45s" string for chart tooltips.
    formatDuration (seconds) {
      const total = Math.max(0, Math.round(Number(seconds) || 0))
      if (!total) return '0s'
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      if (h) return `${h}h ${m}m`
      if (m) return `${m}m ${s}s`
      return `${s}s`
    },
    toIso (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'last-month':
          start.setMonth(today.getMonth() - 1, 1)
          end.setMonth(today.getMonth(), 0)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      if (range.fromDate !== this.fromDate || range.toDate !== this.toDate) {
        this.fromDate = range.fromDate
        this.toDate = range.toDate
        this.invalidateCaches()
        this.loadActiveTab()
      }
    }
  }
}
</script>

<style scoped>
.bsr-page { padding: 0.25rem 0; }

/* Compact single-row topbar: brand on the left, tab pills on the right.
   The old multi-line toolbar (h1 + subtitle + separate yellow note + tab
   bar row) was eating ~320px of vertical space before the first KPI tile.
   The description and the branch-rollout caveat now live in a tooltip on
   the (i) button next to the title. */
.bsr-topbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; padding: 0.25rem 0.25rem 0.6rem; flex-wrap: wrap;
}
.bsr-topbar__brand { display: flex; align-items: center; gap: 0.55rem; min-width: 0; }
.bsr-toolbar__accent {
  width: 4px; height: 26px; border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}
.bsr-topbar__brand h1 {
  margin: 0; font-size: 1.15rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em; line-height: 1;
}
.bsr-topbar__info {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 999px;
  border: 1px solid #d6eee3; background: #f5fbf8; color: #0e5b46;
  cursor: help; padding: 0; flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.bsr-topbar__info:hover { background: #ecfdf5; border-color: #16a07a; }
.bsr-topbar__info:focus { outline: none; }
.bsr-topbar__info:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bsr-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.page-tab-bar {
  display: inline-flex; align-items: center;
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 999px; padding: 0.2rem;
  margin: 0; gap: 0.2rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  max-width: 100%;
  overflow-x: auto; scrollbar-width: none;
}
.page-tab-bar::-webkit-scrollbar { display: none; }
.page-tab {
  display: inline-flex; align-items: center;
  height: 30px; padding: 0 0.95rem;
  border: 0; background: transparent;
  border-radius: 999px;
  color: #64748b; font-size: 0.82rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.page-tab:hover:not(.is-active) { background: #f1f5f9; color: #334155; }
.page-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff; box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}
.page-tab:focus { outline: none; }
.page-tab:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.page-tab.is-active:focus-visible {
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bsr-section {
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}
.bsr-section__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.65rem; padding: 0.65rem 1rem;
  border-bottom: 1px solid #eef1f7; background: #fbfcfe;
  border-top-left-radius: 12px; border-top-right-radius: 12px;
}
.bsr-section__head h2 {
  margin: 0; font-size: 0.92rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em;
}
.bsr-section__hint { font-size: 0.74rem; color: #6b7280; }
.bsr-head-actions { display: inline-flex; align-items: center; gap: 0.55rem; min-width: 0; }
/* "View all" pill in a leaderboard card header — opens the full-list pane. */
.bsr-viewall {
  display: inline-flex; align-items: center;
  height: 24px; padding: 0 0.6rem; border-radius: 999px;
  border: 1px solid #d6eee3; background: #f5fbf8; color: #0e5b46;
  font-family: inherit; font-size: 0.7rem; font-weight: 700;
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.bsr-viewall:hover { background: #ecfdf5; border-color: #16a07a; color: #0e5b46; }
.bsr-viewall:focus { outline: none; }
.bsr-viewall::-moz-focus-inner { border: 0; }
.bsr-viewall:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.bsr-section__body { padding: 1rem; }
.bsr-section--chart .bsr-section__body { padding: 0.75rem 0.75rem 0.5rem; }

.bsr-chart-body { min-height: 280px; }

/* Inline filter bar — date picker, preset pills, range chip, Search button
   on a single row. No card heading, no per-field labels. Saves another
   ~70px vs the boxed "Filters" section that used to wrap these. */
.bsr-filterbar {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 12px; padding: 0.45rem 0.6rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 0.85rem;
}
.bsr-filterbar__spacer { flex: 1 1 auto; min-width: 0.5rem; }
.bsr-filterbar__range {
  font-size: 0.74rem; color: #6b7280;
  white-space: nowrap; font-variant-numeric: tabular-nums;
}
/* DateRangePicker form variant defaults to width: 100% so it fits a form
   grid column — but in this flex bar that pushes presets and the Search
   button to a second row. Constrain to its natural content width so
   everything sits on one line. */
.bsr-filterbar >>> .date-range--form { width: auto; }
.bsr-filterbar >>> .date-range--form .date-range-anchor { width: auto; }
.bsr-filterbar >>> .date-range--form .date-range-shell { width: auto; min-width: 280px; }

.bsr-presets {
  display: flex; gap: 0.3rem; flex-wrap: wrap; align-items: center;
}
.bsr-preset {
  height: 28px; padding: 0 0.7rem;
  border: 1px solid #e5e9f2; border-radius: 999px;
  background: #ffffff; color: #4b5563;
  font-size: 0.72rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.bsr-preset:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.bsr-preset.is-active {
  border-color: #16a07a; color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}
.bsr-preset:focus { outline: none; }
.bsr-preset:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bsr-search-btn {
  display: inline-flex; align-items: center; gap: 0.45rem;
  height: 36px; padding: 0 1.1rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #ffffff;
  font-family: inherit; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.bsr-search-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}
.bsr-search-btn:disabled { opacity: 0.7; cursor: progress; }
.bsr-search-btn:focus { outline: none; }
.bsr-search-btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bsr-totals { display: inline-flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.bsr-totals__chip {
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.18rem 0.6rem; border-radius: 999px;
  background: #ecfdf5; color: #0e5b46;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.02em;
}
.bsr-totals__chip strong {
  color: #1f2a44; font-weight: 700; font-variant-numeric: tabular-nums;
}

.bsr-state-pill {
  display: inline-flex; align-items: center;
  padding: 0.12rem 0.55rem; border-radius: 999px;
  font-size: 0.7rem; font-weight: 700;
  background: #dff5ea; color: #0e5b46; letter-spacing: 0.02em;
}

.bsr-zero { color: #9ca3af; }
.bsr-warn { color: #b45309; font-weight: 700; }

/* ---------- KPI grid (Overview tab) ---------- */
.bsr-kpi-grid {
  display: grid; gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 1rem;
}
.bsr-kpi {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 0.75rem;
  background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.bsr-kpi:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}
.bsr-kpi--compact { padding: 0.65rem 0.8rem; }
.bsr-kpi__text {
  display: flex; flex-direction: column; gap: 0.2rem;
  min-width: 0; flex: 1 1 auto;
}
.bsr-kpi__label {
  font-size: 0.65rem; font-weight: 600; text-transform: uppercase;
  color: #6b7280;
}
.bsr-kpi__value {
  font-size: 1.35rem; font-weight: 800; line-height: 1.15;
  color: #0e5b46; font-variant-numeric: tabular-nums; letter-spacing: -0.02em;
}
.bsr-kpi--compact .bsr-kpi__value { font-size: 1.15rem; }
.bsr-kpi__value--warn { color: #b45309; }
.bsr-kpi__denom { color: #9ca3af; font-weight: 600; font-size: 0.9rem; margin-left: 0.15rem; }
.bsr-kpi__sub { font-size: 0.7rem; color: #6b7280; }

/* Tinted square that holds the metric glyph. Brand-green tint by default
   (matches the green headline number), amber tint for the warn variants
   (Returns / Return Value) so the icon picks up the same colour cue as
   the value. Kept subtle per the "restrain the gradient" rule — solid
   tinted background, no gradient. */
.bsr-kpi__icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 10px;
  background: #ecfdf5; color: #0e5b46;
  flex-shrink: 0;
}
.bsr-kpi__icon--warn { background: #fef3c7; color: #b45309; }
.bsr-kpi--compact .bsr-kpi__icon {
  width: 28px; height: 28px; border-radius: 8px;
}
/* Currency glyph (₹) rendered as text inside the icon tile so we get a
   real Indian-Rupee symbol instead of a Feather-style USD dollar SVG. */
.bsr-kpi__glyph {
  font-size: 1.05rem; font-weight: 800; line-height: 1;
  font-variant-numeric: tabular-nums;
}
.bsr-kpi--compact .bsr-kpi__glyph { font-size: 0.9rem; }

/* ---------- Per-org strip ---------- */
.bsr-orgs-strip {
  display: grid; gap: 0.6rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 1rem;
}
.bsr-org-tile {
  display: flex; align-items: center; gap: 0.7rem;
  padding: 0.7rem 0.85rem; border-radius: 10px;
  background: linear-gradient(135deg, #f5fbf8 0%, #ffffff 100%);
  border: 1px solid #d8eee3;
}
.bsr-org-tile__logo {
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 10px;
  background: #ffffff; border: 1px solid #e5e9f2;
  flex-shrink: 0; overflow: hidden;
}
.bsr-org-tile__logo img {
  width: 100%; height: 100%; object-fit: contain; padding: 4px;
}
.bsr-org-tile__initial {
  font-size: 1rem; font-weight: 800; color: #0e5b46;
  line-height: 1;
}
.bsr-org-tile__body {
  display: flex; flex-direction: column; gap: 0.18rem;
  min-width: 0; flex: 1 1 auto;
}
.bsr-org-tile__name {
  font-size: 0.72rem; font-weight: 700; color: #0e5b46;
  text-transform: uppercase; letter-spacing: 0.03em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.bsr-org-tile__sales {
  font-size: 1.05rem; font-weight: 800; color: #1f2a44;
  font-variant-numeric: tabular-nums;
}
.bsr-org-tile__meta { font-size: 0.7rem; color: #4f5560; }
.bsr-org-tile__returns { color: #b45309; font-weight: 600; }

/* ---------- Leaderboards ---------- */
.bsr-leaderboard-grid {
  display: grid; gap: 0.85rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.bsr-leaderboard { margin-bottom: 0; }
.bsr-rank { list-style: none; padding: 0.35rem 0; margin: 0; }
.bsr-rank__item {
  display: flex; align-items: center; gap: 0.7rem;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: default;
  transition: background 0.12s ease;
}
.bsr-rank__item:last-child { border-bottom: 0; }
.bsr-rank__item:not(.bsr-rank__item--static) { cursor: pointer; }
.bsr-rank__item:not(.bsr-rank__item--static):hover { background: #f5fbf8; }
.bsr-rank__pos {
  flex: 0 0 24px; height: 24px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: #ecfdf5; color: #0e5b46;
  font-size: 0.72rem; font-weight: 800;
}
.bsr-rank__item:first-child .bsr-rank__pos {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
}
.bsr-rank__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 0.1rem; }
.bsr-rank__name {
  font-size: 0.82rem; font-weight: 600; color: #1f2a44;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.bsr-rank__sub {
  font-size: 0.7rem; color: #6b7280;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.bsr-rank__value {
  font-size: 0.85rem; font-weight: 700; color: #0e5b46;
  font-variant-numeric: tabular-nums; flex-shrink: 0;
}
/* Frequent-customers value shows the order COUNT (the ranked metric) with a
   small unit caption underneath, so the headline number reads as a count
   rather than another currency figure. */
.bsr-rank__value--count {
  display: inline-flex; flex-direction: column; align-items: flex-end;
  line-height: 1.05; font-size: 1rem;
}
.bsr-rank__unit {
  font-size: 0.58rem; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: #94a3b8;
}

/* Org badges on the Multi-Org Customers leaderboard — small brand-tinted
   chips listing each organisation the customer bought from (MAP/MDI/MVP). */
.bsr-rank__orgs { display: inline-flex; gap: 0.2rem; vertical-align: middle; }
.bsr-org-badge {
  display: inline-flex; align-items: center;
  padding: 0.02rem 0.32rem; border-radius: 5px;
  background: #ecfdf5; color: #0e5b46;
  font-size: 0.6rem; font-weight: 800; letter-spacing: 0.03em;
}

/* ---------- Mix charts (Payment Mix + Order Type Mix) ---------- */
.bsr-mix-grid {
  display: grid; gap: 0.85rem; margin-top: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}
.bsr-mix-grid .bsr-section { margin-bottom: 0; }
.bsr-mix-body {
  display: flex; flex-direction: column;
  align-items: stretch; justify-content: center;
  min-height: 280px;
}
/* Let the chart canvas span the full card width. Without this the flex
   parent sized each chart to its content width, so a doughnut with a long
   right-hand legend (Payment Mix, with 10+ methods) collapsed to ~0 width
   and only the legend showed. The empty-state <p> re-centres its own text. */
.bsr-mix-body > div { width: 100%; }
.bsr-mix-body > .bsr-empty { align-self: center; }

.bsr-empty { margin: 0; padding: 1rem; color: #6b7280; font-size: 0.82rem; text-align: center; }
.bsr-empty--inline { padding: 0.85rem; }

/* ---------- Skeleton ---------- */
.bsr-skeleton-grid {
  display: grid; gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 1rem;
}
.bsr-skeleton-tile {
  height: 88px; border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: bsr-shimmer 1.4s ease-in-out infinite;
}
@keyframes bsr-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ---------- View-mode toggle bar (Table / Grid) ---------- */
.bsr-view-bar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; margin-bottom: 0.75rem; flex-wrap: wrap;
}
.bsr-view-bar__title {
  display: flex; align-items: center; gap: 0.85rem;
  min-width: 0; flex-wrap: wrap;
}
.bsr-view-bar__title h2 {
  margin: 0; font-size: 0.95rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em;
}
.bsr-view-toggle {
  display: inline-flex; align-items: center; gap: 0.15rem;
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 999px; padding: 0.2rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  flex-shrink: 0;
}
.bsr-view-toggle__btn {
  display: inline-flex; align-items: center; gap: 0.35rem;
  height: 28px; padding: 0 0.75rem;
  border: 0; background: transparent; border-radius: 999px;
  color: #64748b; font-size: 0.74rem; font-weight: 600;
  font-family: inherit; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.bsr-view-toggle__btn:hover:not(.is-active) {
  background: #f1f5f9; color: #334155;
}
.bsr-view-toggle__btn.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff; box-shadow: 0 4px 10px rgba(14, 91, 70, 0.22);
}
.bsr-view-toggle__btn:focus { outline: none; }
.bsr-view-toggle__btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ---------- Card grid (By Branch / By State) ----------
   Layout pattern inspired by profile/listing cards: hero image area on
   top with a floating chip overlay (the most-scanned metric), then a
   clean body with name + tagline + 3-column stat row + dark pill CTA. */
.bsr-card-grid {
  display: grid; gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  margin-bottom: 1rem;
}
.bsr-card {
  position: relative;
  display: flex; flex-direction: column;
  background: #ffffff; border: 1px solid #e5e9f2; border-radius: 18px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  text-align: left; font-family: inherit; color: inherit;
  padding: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
button.bsr-card { border-width: 1px; }
.bsr-card--clickable { cursor: pointer; }
.bsr-card--clickable:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
}
.bsr-card--clickable:focus { outline: none; }
.bsr-card--clickable:focus-visible {
  outline: none;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12), 0 0 0 3px rgba(22, 160, 122, 0.28);
}

/* --- Hero (image area) --- */
.bsr-card__hero {
  position: relative;
  height: 158px;
  margin: 0.6rem 0.6rem 0;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  isolation: isolate;
  background-color: #1f2a44; /* fallback while photo loads */
}
/* When an actual branch photo is present, the gradient is irrelevant —
   the <img> fills the hero. The pattern layer becomes a dark vignette so
   the white "Total Sales" chip and the "Branch" tag stay readable over
   arbitrary photo content. */
.bsr-card__hero--photo .bsr-card__hero-pattern {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 35%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0) 30%);
}
.bsr-card__hero-image {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.bsr-card__hero-letter {
  font-size: 5.5rem; font-weight: 900;
  color: rgba(255, 255, 255, 0.95);
  line-height: 0.9; letter-spacing: -0.06em;
  user-select: none;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
  z-index: 1;
}
/* Soft diagonal sheen for a bit of texture on the otherwise-flat
   gradient. Pure CSS, no asset cost. */
.bsr-card__hero-pattern {
  position: absolute; inset: 0;
  background:
    radial-gradient(circle at 20% 100%, rgba(255, 255, 255, 0.18), transparent 40%),
    radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.14), transparent 45%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(0, 0, 0, 0.08));
  pointer-events: none;
}
.bsr-card__hero-chip {
  position: absolute; top: 12px; right: 12px;
  display: flex; flex-direction: column; align-items: flex-end; gap: 0.05rem;
  padding: 0.45rem 0.65rem; border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
  z-index: 2;
  max-width: calc(100% - 100px); /* leave room for the top-left tag pill */
  min-width: 0;
}
.bsr-card__hero-chip-label {
  font-size: 0.6rem; font-weight: 700; color: #4f5560;
  text-transform: uppercase; letter-spacing: 0.05em;
  line-height: 1;
}
.bsr-card__hero-chip-value {
  font-size: 0.92rem; font-weight: 800; color: #0e5b46;
  font-variant-numeric: tabular-nums; line-height: 1.05;
  letter-spacing: -0.01em;
  max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bsr-card__hero-tag {
  position: absolute; top: 12px; left: 12px;
  display: inline-flex; align-items: center;
  padding: 0.2rem 0.55rem; border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff; font-size: 0.62rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  z-index: 2;
}

/* --- Body --- */
.bsr-card__body {
  display: flex; flex-direction: column; gap: 0.65rem;
  padding: 0.85rem 1rem 1rem;
  flex: 1 1 auto;
}
.bsr-card__name-row {
  display: flex; align-items: center; gap: 0.4rem;
  min-width: 0;
}
.bsr-card__name {
  margin: 0; flex: 1 1 auto; min-width: 0;
  font-size: 1.05rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em; line-height: 1.2;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.bsr-card__verify {
  display: inline-flex; align-items: center; justify-content: center;
  color: #16a07a; flex-shrink: 0;
}
.bsr-card__tagline {
  display: inline-flex; align-items: center; gap: 0.3rem; flex-wrap: wrap;
  margin: 0;
  font-size: 0.76rem; color: #6b7280; line-height: 1.35;
}
.bsr-card__tagline-icon { color: #94a3b8; flex-shrink: 0; }
.bsr-card__dot { color: #cbd5e1; padding: 0 0.05rem; }

/* --- 3-column stat row (per-org sales) with vertical dividers --- */
.bsr-card__stats {
  display: grid; grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  margin-top: 0.1rem;
  padding: 0.55rem 0;
  border-top: 1px solid #eef1f7;
  border-bottom: 1px solid #eef1f7;
}
.bsr-card__stat {
  display: flex; flex-direction: column; align-items: center;
  gap: 0.15rem;
  padding: 0.1rem 0.3rem;
  position: relative;
  text-align: center;
  min-width: 0;
}
.bsr-card__stat + .bsr-card__stat::before {
  content: ''; position: absolute; left: 0; top: 4px; bottom: 4px;
  width: 1px; background: #eef1f7;
}
.bsr-card__stat strong {
  font-size: 0.9rem; font-weight: 800; color: #1f2a44;
  font-variant-numeric: tabular-nums; line-height: 1.1;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}
.bsr-card__stat span {
  font-size: 0.62rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.04em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}
.bsr-card__stat.is-zero strong { color: #cbd5e1; }
.bsr-card__stat.is-zero span   { color: #94a3b8; }

/* --- CTA pill (only on clickable cards) ---
   Default: white pill with brand-green text + thin brand-tinted border.
   Hover: flips to the brand gradient + white text + lifted shadow, so
   the colour change reinforces that the whole card is clickable. */
.bsr-card__cta {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 0.5rem;
  height: 42px; padding: 0 1.25rem; border-radius: 999px;
  background: #ffffff; color: #0e5b46;
  border: 1px solid #d6eee3;
  font-size: 0.85rem; font-weight: 700; letter-spacing: 0.01em;
  margin-top: auto;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.bsr-card__cta svg { transition: transform 0.18s ease; }
.bsr-card--clickable:hover .bsr-card__cta {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 8px 18px rgba(14, 91, 70, 0.28);
}
.bsr-card--clickable:hover .bsr-card__cta svg {
  transform: translateX(3px);
}

.bsr-card-empty {
  margin: 0 0 1rem; padding: 2rem 1rem;
  background: #ffffff; border: 1px solid #e5e9f2; border-radius: 18px;
  color: #6b7280; font-size: 0.85rem; text-align: center;
}

/* Skeleton card (initial load) — block-shaped like the real card */
.bsr-card--skeleton {
  height: 340px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: bsr-shimmer 1.4s ease-in-out infinite;
  border: 0;
}

/* Hero gradient palette — same deterministic name-hash → tone mapping,
   subdued enough that a wall of cards stays calm rather than carnival. */
.bsr-card__hero--green  { background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); }
.bsr-card__hero--teal   { background: linear-gradient(135deg, #0e7490 0%, #14b8a6 100%); }
.bsr-card__hero--indigo { background: linear-gradient(135deg, #3730a3 0%, #6366f1 100%); }
.bsr-card__hero--amber  { background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%); }
.bsr-card__hero--slate  { background: linear-gradient(135deg, #334155 0%, #64748b 100%); }
.bsr-card__hero--plum   { background: linear-gradient(135deg, #831843 0%, #be185d 100%); }

/* ---------- Clickable branch row + chevron ---------- */
.bsr-row-clickable >>> td { cursor: pointer; }
.bsr-row-clickable:hover >>> td { background: #f5fbf8 !important; }
.bsr-branch-cell {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-weight: 600; color: #1f2a44;
}
.bsr-branch-cell__chev { color: #16a07a; opacity: 0.7; }

/* ---------- Slide-over drill-down ---------- */
/* Slide-over stack. Branch drill sits on top of everything (it can be
   launched from the list pane), then the entity detail, then the list
   pane underneath. */
.bsr-drill {
  position: fixed; inset: 0;
  z-index: 1400;
  display: flex; justify-content: flex-end;
}
.bsr-overlay {
  position: fixed; inset: 0;
  display: flex; justify-content: flex-end;
}
.bsr-overlay--list { z-index: 1200; }
.bsr-overlay--entity { z-index: 1300; }
.bsr-entity__breakdowns {
  display: grid; gap: 0.85rem; margin-bottom: 0.85rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}
.bsr-entity__breakdowns .bsr-section { margin-bottom: 0; }
.bsr-drill__backdrop {
  position: absolute; inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}
.bsr-drill__panel {
  position: relative;
  width: min(1100px, 95vw);
  height: 100%;
  background: #f7f8fb;
  box-shadow: -16px 0 40px rgba(15, 23, 42, 0.22);
  display: flex; flex-direction: column;
}
.bsr-drill__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 0.75rem; padding: 1rem 1.15rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  flex-shrink: 0;
}
.bsr-drill__avatar {
  display: inline-flex; align-items: center; justify-content: center;
  width: 56px; height: 56px; border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  flex-shrink: 0; overflow: hidden;
  color: #ffffff;
}
.bsr-drill__avatar img {
  width: 100%; height: 100%; object-fit: cover;
}
.bsr-drill__avatar-initial {
  font-size: 1.6rem; font-weight: 900;
  letter-spacing: -0.04em; line-height: 1;
}
.bsr-drill__head-text { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; flex: 1 1 auto; }
.bsr-drill__eyebrow {
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; opacity: 0.85;
}
.bsr-drill__head h2 {
  margin: 0; font-size: 1.15rem; font-weight: 700;
  letter-spacing: -0.01em; line-height: 1.2;
}
.bsr-drill__sub { font-size: 0.78rem; opacity: 0.9; }
.bsr-drill__close {
  flex-shrink: 0;
  width: 32px; height: 32px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.15); border: 0;
  color: #ffffff; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.15s ease;
}
.bsr-drill__close:hover { background: rgba(255, 255, 255, 0.28); }
.bsr-drill__close:focus { outline: none; }
.bsr-drill__close:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55);
}
.bsr-drill__body {
  flex: 1 1 auto; overflow-y: auto; padding: 1rem 1.15rem;
}
.bsr-drill__loading {
  display: grid; gap: 0.6rem;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}
.bsr-drill__kpis {
  display: grid; gap: 0.6rem;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin-bottom: 0.85rem;
}
.bsr-drill__orgs {
  display: grid; gap: 0.6rem;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin-bottom: 0.85rem;
}
.bsr-orgstat {
  display: flex; flex-direction: column; gap: 0.15rem;
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 10px; padding: 0.6rem 0.75rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}
.bsr-orgstat__code {
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: #16a07a;
}
.bsr-orgstat__value { font-size: 1.05rem; font-weight: 700; color: #1f2a44; }
.bsr-orgstat__sub   { font-size: 0.72rem; color: #94a3b8; }
.bsr-orgstat.is-zero .bsr-orgstat__code  { color: #cbd5e1; }
.bsr-orgstat.is-zero .bsr-orgstat__value { color: #cbd5e1; }
.bsr-drill__tabs {
  display: inline-flex; align-items: center;
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 999px; padding: 0.25rem;
  margin: 0 0 0.85rem; gap: 0.2rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content; max-width: 100%;
  overflow-x: auto; scrollbar-width: none;
}

/* Export-to-Excel button (brand-gradient pill), shared by the drill-down
   per-tab toolbar. */
.bsr-drill__toolbar { display: flex; justify-content: flex-end; margin: 0 0 0.75rem; }
.bsr-export-btn {
  display: inline-flex; align-items: center; gap: 0.45rem;
  height: 34px; padding: 0 0.95rem; border-radius: 999px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #ffffff;
  font-family: inherit; font-size: 0.76rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.bsr-export-btn--sm { height: 30px; padding: 0 0.8rem; font-size: 0.73rem; }
.bsr-export-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}
.bsr-export-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.bsr-export-btn:focus { outline: none; }
.bsr-export-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.bsr-drill__tabs::-webkit-scrollbar { display: none; }
.bsr-drill__tab {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 30px; padding: 0 0.85rem;
  border: 0; background: transparent; border-radius: 999px;
  color: #64748b; font-size: 0.8rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}
.bsr-drill__tab:hover:not(.is-active) { background: #f1f5f9; color: #334155; }
.bsr-drill__tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff; box-shadow: 0 4px 12px rgba(14, 91, 70, 0.22);
}
.bsr-drill__tab:focus { outline: none; }
.bsr-drill__tab:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.bsr-drill__tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 0.3rem;
  border-radius: 999px; font-size: 0.66rem; font-weight: 700;
  background: rgba(255, 255, 255, 0.18); color: inherit;
}
.bsr-drill__tab:not(.is-active) .bsr-drill__tab-count {
  background: #eef1f7; color: #6b7280;
}

.bsr-staff-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.bsr-staff-cell__name { font-weight: 600; color: #1f2a44; }
.bsr-staff-cell__role { font-size: 0.7rem; color: #6b7280; }

.bsr-item-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.bsr-item-cell__name { font-weight: 600; color: #1f2a44; }
.bsr-item-cell__sku {
  font-size: 0.68rem; color: #6b7280;
  font-family: ui-monospace, 'SF Mono', Consolas, monospace;
}

.bsr-order-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.bsr-order-cell__num { font-weight: 600; color: #1f2a44; font-variant-numeric: tabular-nums; }
.bsr-order-cell__cn {
  font-size: 0.68rem; color: #b45309; font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* ---------- Dashboard-parity drill-down cells ---------- */
/* Care-of columns: a muted dash reads cleaner than a column of zeros;
   signed net c/o (received − handed off) shows negatives in red. */
.bsr-co-muted { color: #b8bfca; }
.bsr-co-neg { color: #dc2626; font-weight: 600; }

/* Sales-By-Person toolbar search — sits inline in the table header. */
.sbp-search {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 220px;
}
.sbp-search__icon {
  position: absolute;
  left: 0.55rem;
  color: #94a3b8;
  pointer-events: none;
}
.sbp-search__input {
  width: 100%;
  height: 30px;
  padding: 0 1.85rem 0 1.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.8rem;
  color: #1f2a44;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sbp-search__input::placeholder { color: #94a3b8; }
.sbp-search__input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}
.sbp-search__input::-webkit-search-cancel-button { display: none; }
.sbp-search__clear {
  position: absolute;
  right: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #64748b;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.sbp-search__clear:hover { background: #f1f5f9; color: #1f2a44; }
.sbp-search__clear:focus { outline: none; }
.sbp-search__clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* Customer cell (Returns tab) — name over phone. */
.bsr-cust-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.bsr-cust-cell__name { font-weight: 600; color: #1f2a44; }
.bsr-cust-cell__phone {
  font-size: 0.68rem; color: #6b7280;
  font-variant-numeric: tabular-nums;
}

/* Returned-products list rendered inside a single cell. */
.bsr-ret-products { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.15rem; }
.bsr-ret-products__item {
  display: inline-flex; align-items: baseline; gap: 0.35rem;
  max-width: 260px;
}
.bsr-ret-products__name {
  color: #1f2a44; font-size: 0.76rem;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bsr-ret-products__qty {
  color: #6b7280; font-size: 0.7rem; font-weight: 600;
  font-variant-numeric: tabular-nums; flex-shrink: 0;
}

/* ---------- Staff × Product matrix ---------- */
.bsr-matrix-wrap { margin-bottom: 1rem; }
.bsr-matrix-scroll {
  overflow-x: auto; padding: 0;
  scrollbar-width: thin;
}
.bsr-matrix {
  width: 100%; min-width: 100%;
  border-collapse: collapse; font-size: 0.78rem;
}
.bsr-matrix thead th {
  background: #fbfcfe; color: #4f5560;
  font-weight: 700; font-size: 0.7rem;
  letter-spacing: 0.03em; text-transform: uppercase;
  padding: 0.55rem 0.7rem; text-align: right;
  border-bottom: 1px solid #eef1f7;
  white-space: nowrap;
  position: sticky; top: 0; z-index: 1;
}
.bsr-matrix__staff-head {
  text-align: left !important;
  position: sticky; left: 0; z-index: 2;
  background: #fbfcfe;
  min-width: 160px;
}
.bsr-matrix__prod-head {
  max-width: 140px; overflow: hidden; text-overflow: ellipsis;
}
.bsr-matrix tbody tr:hover td { background: #f5fbf8; }
.bsr-matrix tbody tr:nth-child(even) td { background: #fcfdff; }
.bsr-matrix tbody tr:nth-child(even):hover td { background: #f5fbf8; }
.bsr-matrix__staff {
  font-weight: 600; color: #1f2a44;
  padding: 0.55rem 0.7rem;
  border-bottom: 1px solid #eef1f7;
  position: sticky; left: 0; z-index: 1;
  background: #ffffff;
  min-width: 160px;
}
.bsr-matrix__cell {
  padding: 0.45rem 0.7rem; text-align: right;
  border-bottom: 1px solid #eef1f7;
  font-variant-numeric: tabular-nums;
}
.bsr-matrix__qty { display: block; font-weight: 700; color: #1f2a44; }
.bsr-matrix__sales { display: block; font-size: 0.68rem; color: #0e5b46; font-weight: 600; }
.bsr-matrix__zero { color: #cbd5e1; }

/* ---------- Slide-over transition ---------- */
.bsr-slide-enter-active, .bsr-slide-leave-active {
  transition: opacity 0.2s ease;
}
.bsr-slide-enter, .bsr-slide-leave-to { opacity: 0; }
.bsr-slide-enter-active .bsr-drill__panel,
.bsr-slide-leave-active .bsr-drill__panel {
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}
.bsr-slide-enter .bsr-drill__panel,
.bsr-slide-leave-to .bsr-drill__panel {
  transform: translateX(100%);
}

@media (max-width: 1100px) {
  .bsr-leaderboard-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767px) {
  .bsr-topbar { gap: 0.5rem; padding-bottom: 0.5rem; }
  .bsr-filterbar { padding: 0.55rem; }
  .bsr-filterbar__spacer { display: none; }
  .bsr-search-btn { flex: 1 1 100%; justify-content: center; }
  .bsr-drill__panel { width: 100vw; }
  .bsr-leaderboard-grid { grid-template-columns: 1fr; }
  .bsr-card-grid { grid-template-columns: 1fr; }
  .bsr-view-bar { gap: 0.5rem; }
}
</style>
